import { CareEnvironment, CarePhase, FundingPolicyType } from '../../appModel';
import { calcAnnuityProjectedROI } from './calcFundingAnnuityDerivatives';
import { calcFundingAnnuityDerivatives } from './calcFundingAnnuityDerivatives';
import { calcFutureValue } from '../utils/calcFutureValue';
import { calcFutureValueSimpleInflation } from '../utils/calcFutureValueSimpleInflation';
import { calcPolicyCoverageOfRemainingLtcCosts } from './calcFundingPolicyOnClaimDerivatives';
import { calcPolicyProjectedCostCoverage } from './calcFundingPolicyPreClaimDerivatives';
import {
  calcIncomeStreamProjectedCoverage,
  calcSelfFundingProjectedCostCoverage,
  calcSelfFundingProjectedROI,
  calcSelfFundingTotalCost,
  calcSelfFundingTotalsOnClaim,
  getCalcConfig,
} from './calcFundingSelfDerivatives';
import { calcTotalWithAnnualIncrease } from '../utils/calcTotalWithAnnualIncrease';
import { carePhaseDefs } from '../../CarePhaseDef';
import {
  checkPolicyCalculationTypeEquals,
  fundingPolicyTypeDefs,
  hasFlatDeathBenefit,
  isMembershipProgram,
  isCareMattersFamily,
} from '../../FundingPolicyTypeDef';
import {
  CareMattersRiderForm,
  calcCareMattersDeathBenefit,
  calcTotalLtcBenefitOverPeriod,
  simulateCareMattersPhase,
} from './calcCareMattersCoverage';
import { isCarePhasePast } from '../../isCarePhasePast';
import { isNullOrUndefined } from '../../isNullOrUndefined';
import { Draft } from 'immer';
import { WritableDraft } from 'immer/dist/internal.js';
import { cloneDeep } from 'lodash';
import { calcLtcEndYear } from '../utils/calcLtcEndYear';
import { resolveUseClaimProjectionEngine } from '../utils/resolveUseClaimProjectionEngine';
import { analyzeInvestmentReturns } from '../roi/calcProjectedROI';
import { calcClientPhaseStartYears } from '../../clientPhaseStartYears';
import {
  JointProjectionContext,
  processClaimProjection,
} from './projection/processClaimProjection';
import { buildJointMonthlyCareInputs } from './projection/monthlyCareInputCalculations';
import { PolicyProjectionStrategy } from './projection/strategyTypes';
import { isIndependentLivingBenefitZeroedOut } from './projection/sharedPolicyConfig';
import { cmTwoStrategy } from './projection/strategies/cmTwoStrategy';
import { nylAssetFlexStrategy } from './projection/strategies/nylAssetFlexStrategy';
import { oaAssetCareStrategy } from './projection/strategies/oaAssetCareStrategy';
import { hybridLifeIraStrategy } from './projection/strategies/hybridLifeIraStrategy';
import { traditionalLtcStrategy } from './projection/strategies/traditionalLtcStrategy';
import { forecareStrategy } from './projection/strategies/forecareStrategy';
import { annuityCareTwoStrategy } from './projection/strategies/annuityCareTwoStrategy';
import { indexedAnnuityCareStrategy } from './projection/strategies/indexedAnnuityCareStrategy';
import { lincolnMoneyGuardStrategy } from './projection/strategies/lincolnMoneyGuardStrategy';
import { lifeWithRiderStrategy } from './projection/strategies/lifeWithRiderStrategy';
import { genericHybridStrategy } from './projection/strategies/genericHybridStrategy';
import { membershipProgramStrategy } from './projection/strategies/membershipProgramStrategy';
import { shortTermCareStrategy } from './projection/strategies/shortTermCareStrategy';
import { unlimitedMaxBenefitMagicNumber } from './projection/sharedConstants';
import { BuildEnvironment, builtEnvironment } from '../../builtEnvironment';
import { isRunningLocal } from '../../isRunningEnvironment';
import { coverageKeyFor, projectionToLegacy } from './projection/projectionToLegacy';
import type { ClientDerivativeOptions } from '../calcClientDerivatives';

// Routes a policy to its projection strategy for shadow-mode runs. Returns
// null when the product isn't yet ported (legacy calcs handle it).
export function pickStrategy(
  policyType: FundingPolicyType | null | undefined,
  isJoint: boolean,
): PolicyProjectionStrategy | null {
  // Joint is ported for exactly two products: CareMatters II Together
  // (isCareMattersFamily covers careMattersTwo + jointCareMatters) and Asset
  // Care joint. These checks fire for both the single-life and joint variants.
  if (isCareMattersFamily(policyType)) return cmTwoStrategy;
  if (
    policyType === FundingPolicyType.assetCare ||
    policyType === FundingPolicyType.jointAssetCare
  ) {
    return oaAssetCareStrategy;
  }
  // OneAmerica Indexed Annuity Care: annuity chassis with scheduled base LTCBB
  // and a COB rider. It used to ride the hybridLifeIra legacy path, but the
  // carrier illustration shows different annuity mechanics.
  if (policyType === FundingPolicyType.indexedAnnuityCare) {
    return indexedAnnuityCareStrategy;
  }
  // Hybrid Life IRA family — OneAmerica Asset Care III plus the generic
  // hybridLifeIra type. IRA-funded Asset Care variant: two
  // pools (Face/AOB + COB) with a withdrawal-rate-driven base payout. These
  // single-life types share the same legacy `hybridLifeIra` calc path, so they
  // route together for parity. MODELING IS UNVERIFIED (no carrier illustration
  // for Asset Care III; flagged `hasUnverifiedModeling`)
  // — the strategy mirrors legacy faithfully. The joint variants
  // (jointHybridLifeIra / jointAssetcareThree) are deferred and fall through to
  // the joint guard. Placed before the joint guard alongside the other
  // single-life products. See docs/plans/hybridLifeIra-strategy-plan.md.
  if (
    policyType === FundingPolicyType.hybridLifeIra ||
    policyType === FundingPolicyType.assetcareThree
  ) {
    return hybridLifeIraStrategy;
  }
  // Global Atlantic ForeCare (single-pool annuity hybrid). The generic
  // `annuityHybrid` type routes here too as the closest single-pool baseline.
  // Joint variants and the AnnuityCare II / EquiTrust Bridge products stay on
  // legacy / their own strategies (they diverge mechanically). Placed before
  // the joint guard so the single-life variants route here.
  if (
    policyType === FundingPolicyType.forecareFixedAnnuity ||
    policyType === FundingPolicyType.annuityHybrid
  ) {
    return forecareStrategy;
  }
  // OneAmerica AnnuityCare II (two-pool annuity hybrid: LTCAV + COB). Genuine
  // two-pool product (Total LTC = LTCAV + COB) that the single-pool legacy
  // path under-states ~3-4× by never seeing the COB pool. Single-life only —
  // no joint variant. Placed before the joint guard alongside ForeCare.
  if (policyType === FundingPolicyType.annuityCareTwo) {
    return annuityCareTwoStrategy;
  }
  // Life insurance with an LTC acceleration rider — a single death-benefit
  // pool accelerated for LTC (the DB IS the pool; eob ≡ 0). One strategy
  // serves all three single-life products that share
  // `calculationType === lifeInsuranceWithRider`: the generic
  // `lifeInsuranceWithRider`, Transamerica FFIUL (`lifeWithIndexAccount`), and
  // Nationwide No-Lapse Guarantee UL II (`noLapseGuaranteeLife`). Single-life
  // only (no joint variant) — placed before the joint guard alongside ForeCare.
  if (
    policyType === FundingPolicyType.lifeInsuranceWithRider ||
    policyType === FundingPolicyType.lifeWithIndexAccount ||
    policyType === FundingPolicyType.noLapseGuaranteeLife
  ) {
    return lifeWithRiderStrategy;
  }
  // CCAH / Continuing Care at Home membership programs (Fortified Life,
  // Stay Your Way). These share `calculationType === longTermCareInsurance`
  // but are NOT the bare `longTermCareInsurance` policy type — they are
  // distinct enum values routed via `isMembershipProgram`. This branch MUST
  // come before any broadening of the trad-LTC check below to a
  // calculationType match, so membership programs never fall into the generic
  // standalone-LTC path (they have no 2-ADL gate, no waiting period). Both
  // membership types are single-life — placed before the joint guard.
  if (isMembershipProgram(policyType)) return membershipProgramStrategy;
  // Joint standalone-LTC ShareCare products (NY Life SecureCare joint, NGL
  // EssentialLTC joint, Thrivent LTC joint, Mutual of Omaha MutualCare joint).
  // These use the legacy ShareCare 1/3 shared-pool model, ported into the
  // joint-aware traditionalLtcStrategy. All four share
  // `genericType === jointLongTermCareInsurance` and the "shared benefits"
  // convention (the combined household pool splits client-individual +
  // partner-individual + shared). The single-life variants (nylSecureCare /
  // nglEssentialLtc / thriventLongTermCareInsurance / mutualOfOmahaMutualcare)
  // route to the same strategy via the standalone-LTC branch below. The generic
  // `jointLongTermCareInsurance` is NOT in THIS branch — it gets no 1/3 split —
  // but it IS routed to traditionalLtcStrategy via its own branch just below (the
  // plain single-pool joint path, mirroring legacy). Placed above the joint guard
  // alongside the other ported joint products. Each type added HERE MUST also be
  // added to `policyTypeHasSharedPool` in traditionalLtcStrategy so the 1/3 split
  // actually fires.
  if (
    policyType === FundingPolicyType.nylJointSecureCare ||
    policyType === FundingPolicyType.jointNglEssentialLtc ||
    policyType === FundingPolicyType.jointThriventLongTermCareInsurance ||
    policyType === FundingPolicyType.jointMutualOfOmahaMutualcare
  ) {
    return traditionalLtcStrategy;
  }
  // Generic Joint Traditional LTC (`jointLongTermCareInsurance`) — the joint
  // counterpart of the single-life `longTermCareInsurance` type. Legacy runs it
  // through the joint accumulator as a single combined pool spent down across
  // both spouses, WITHOUT the ShareCare 1/3 split (see `policyTypeHasSharedPool`
  // in calcFundingSourceDerivatives / traditionalLtcStrategy, gated only on the
  // two carrier ShareCare products). It therefore routes to the joint-aware
  // traditionalLtcStrategy but is intentionally absent from the shared-pool list,
  // so it takes the plain single-pool joint path — mirroring legacy.
  if (policyType === FundingPolicyType.jointLongTermCareInsurance) {
    return traditionalLtcStrategy;
  }
  // Joint generic hybrid life (`jointHybridAssetBased`) — the joint counterpart
  // of the single-life `hybridLifeInsurance` family. The joint-aware
  // genericHybridStrategy shares the single death-benefit + COB pools across
  // both spouses (each serves their own elimination period), mirroring joint
  // Asset Care. Placed above the joint guard alongside the other ported joint
  // products.
  if (policyType === FundingPolicyType.jointHybridAssetBased) {
    return genericHybridStrategy;
  }
  // Every other joint product still falls back to the legacy aggregate calc.
  if (isJoint) return null;
  if (policyType === FundingPolicyType.nylAssetFlex) return nylAssetFlexStrategy;
  if (policyType === FundingPolicyType.moneyGuardFixedAdvantage) return lincolnMoneyGuardStrategy;
  // Generic two-pool hybrid life insurance (DB/base pool + COB pool). The death
  // benefit is held STATIC by default (like CM II and the other hybrids) — the
  // inflation rider grows the LTC pools, not the life face.
  //
  // Two groups route here:
  //  - The four named carrier products with no illustration in this repo —
  //    Allstate Whole Life, Securian SecureCare III/IV, and Trustmark Life + Care
  //    (`agisGroupPolicy`). These model real carriers we cannot verify, so their
  //    defs carry `hasUnverifiedModeling: true` and surface the disclaimer.
  //  - The generic, user-selectable `hybridLifeInsurance` (and its joint
  //    counterpart `jointHybridAssetBased`, routed above the joint guard). These
  //    are intentionally NOT flagged `hasUnverifiedModeling`: they are BYO generic
  //    hybrids the advisor parameterizes themselves, not a Waterlily attempt to
  //    model a specific carrier product, so the unverified-modeling disclaimer
  //    does not apply.
  if (
    policyType === FundingPolicyType.allstateHybridPolicy ||
    policyType === FundingPolicyType.secureCareThree ||
    policyType === FundingPolicyType.secureCareFour ||
    policyType === FundingPolicyType.agisGroupPolicy ||
    policyType === FundingPolicyType.hybridLifeInsurance
  ) {
    return genericHybridStrategy;
  }
  // Generic standalone-LTC products that all share the same calc path: their
  // FundingPolicyTypeDef calculationType is longTermCareInsurance and they run
  // the identical single-life legacy path (single dollar pool, no second pool,
  // no death benefit). CareScout Care Assurance, NYL Secure Care, Thrivent LTC,
  // NGL EssentialLTC, and Mutual of Omaha MutualCare all drop into the same
  // strategy unchanged. Single-life variants only — the joint variants
  // (nylJointSecureCare / jointNglEssentialLtc) are handled by the joint branch
  // above.
  if (
    policyType === FundingPolicyType.longTermCareInsurance ||
    policyType === FundingPolicyType.careScoutCareAssurance ||
    policyType === FundingPolicyType.nylSecureCare ||
    policyType === FundingPolicyType.thriventLongTermCareInsurance ||
    policyType === FundingPolicyType.nglEssentialLtc ||
    policyType === FundingPolicyType.mutualOfOmahaMutualcare
  ) {
    return traditionalLtcStrategy;
  }
  // Short-Term Care Insurance — traditional/standalone LTC plus a hard
  // benefit-period (day) cap. Single-life only (no joint def). Routed below the
  // joint guard alongside the other single-life products.
  if (policyType === FundingPolicyType.shortTermCareInsurance) {
    return shortTermCareStrategy;
  }
  return null;
}

// Import and re-export from fundingTypes for backward compatibility
import {
  calculateMonthsBetweenYears as _calculateMonthsBetweenYears,
  ReturnOnInvestmentCalculations,
} from './fundingTypes';
export type { ReturnOnInvestmentCalculations } from './fundingTypes';
export { calculateMonthsBetweenYears } from './fundingTypes';
const calculateMonthsBetweenYears = _calculateMonthsBetweenYears;

export { unlimitedMaxBenefitMagicNumber };

const careEnvironmentToDailyMaxKey: Record<CareEnvironment, keyof PolicyFundingSource> = {
  [CareEnvironment.home]: 'dailyHomeBenefitAmount',
  [CareEnvironment.fullCareFacility]: 'dailyNursingHomeBenefitAmount',
  [CareEnvironment.assistedLiving]: 'dailyAssistedLivingBenefitAmount',
  [CareEnvironment.independentLiving]: 'dailyIndependentLivingBenefitAmount',
};

const careEnvironmentToInflationProtectionKeys: Record<CareEnvironment, keyof PolicyFundingSource> =
  {
    [CareEnvironment.home]: 'dailyHomeInflationProtectionPercent',
    [CareEnvironment.independentLiving]: 'dailyIndependentLivingInflationProtectionPercent',
    [CareEnvironment.assistedLiving]: 'dailyAssistedLivingInflationProtectionPercent',
    [CareEnvironment.fullCareFacility]: 'dailyNursingHomeInflationProtectionPercent',
  };

export function calcFundingSourceDerivatives(
  mutableClient: Draft<Client>,
  options: ClientDerivativeOptions = {},
) {
  const commonCalculations = [
    calcFundingAnnuityDerivatives,
    calcCombinedFundingTotalCost,
    calcCombinedProjectedCostCoverage,
    calcCombinedNonLtcPayout,
    calcCombinedTotalValue,
  ];

  calcSourceDefaults(mutableClient);
  runPolicyCalculations(mutableClient, options);
  runSelfFundingCalculations(mutableClient);
  commonCalculations.forEach(calc => calc(mutableClient));
  calcReturnOnInvestment(mutableClient);
}

export function runPolicyCalculations(
  mutableClient: Draft<Client>,
  options: ClientDerivativeOptions = {},
) {
  const {
    intakeSurvey: { clientHasStartedLtc },
    multipleFundingSources,
    clientCalculationSettings: { discountRate },
    allPhaseCosts: { allPhaseInflatedProfessionalShareCostDiscountAmount },
    mutableClientPartner,
  } = mutableClient;

  const calcProjectedCostCoverageFn = clientHasStartedLtc
    ? calcPolicyCoverageOfRemainingLtcCosts
    : calcPolicyProjectedCostCoverage;

  // Shared resolver — the same gate also selects the Stage-1 phase-cost model
  // (calcInflatedCarePhaseCosts), so claims and care costs always price on the
  // same model within one derivative pass.
  const useClaimProjectionEngine = resolveUseClaimProjectionEngine(mutableClient, options);

  mutableClient.policyPhaseCalculations = {};
  const policyClaimProjections: PolicyClaimProjections = {};
  mutableClient.policyClaimProjections = policyClaimProjections;
  // Reset the partner's joint-projection mirror every pass for the same reason
  // the session client's map is reset above: a policy that was joint-routed on
  // a prior recompute (and produced a mirrored ledger here) but is no longer
  // routed — removed, deactivated, or no longer joint — would otherwise leave a
  // stale entry. The session client's map is reset wholesale; the partner's
  // should match. See plan §3.4.
  if (mutableClientPartner) {
    mutableClientPartner.policyClaimProjections = {};
  }
  const shouldAssertProjectionIdentities =
    isRunningLocal() || builtEnvironment !== BuildEnvironment.prod;

  Object.entries(multipleFundingSources.ltcPolicy).forEach(
    ([fundingSourceId, policyFundingSource]) => {
      // initialize ltcPolicy and phaseCalculations
      mutableClient.fundingSources.ltcPolicy = cloneDeep(policyFundingSource);
      mutableClient.phaseCalculations = [];
      // Align shadow projection inputs with the legacy calc entry state.
      // Without this reset, reruns can feed stale `...WaitingPeriodDaysRemaining`
      // counters (mutated by prior legacy passes) into strategies and
      // incorrectly skip elimination-period months.
      initializeRemainingFields(mutableClient);

      // Shadow-mode claim projection run for ported products (see pickStrategy;
      // includes single-life + joint CM II / Asset Care). Runs BEFORE legacy
      // calcs so the strategy sees the unmutated cloned ltcPolicy. Keyed by the
      // outer-loop fundingSourceId.
      const ltcPolicyForProjection = mutableClient.fundingSources.ltcPolicy;
      // Captured outside the try so the coverage step below can swap in the
      // adapter (flag on) when it succeeded.
      let claimProjection: ClaimProjection | undefined;
      let projectionIsJoint = false;
      try {
        const isJoint = isJointPolicy(
          ltcPolicyForProjection.policyType ?? null,
          mutableClient.mutableClientPartner,
        );
        const strategy = pickStrategy(ltcPolicyForProjection.policyType, isJoint);
        // CM II also requires a non-zero benefit period (legacy
        // shouldUseCareMattersCalc gate). Asset Flex has no such gate.
        const cmTwoEligible =
          strategy !== cmTwoStrategy || shouldUseCareMattersCalc(ltcPolicyForProjection);
        if (strategy && cmTwoEligible) {
          const joint = isJoint
            ? buildJointProjectionContext(mutableClient, ltcPolicyForProjection)
            : undefined;
          const { projection } = processClaimProjection(
            mutableClient,
            ltcPolicyForProjection,
            strategy,
            { assertIdentities: shouldAssertProjectionIdentities, joint },
          );
          policyClaimProjections[fundingSourceId] = projection;
          claimProjection = projection;
          projectionIsJoint = isJoint;
          // Mirror the joint ledger onto the partner so the projection-details
          // UI can render from either spouse's perspective (plan §3.4). The
          // partner's map is reset at the top of this function, so no lazy
          // initialization is needed here.
          if (joint) {
            mutableClient.mutableClientPartner!.policyClaimProjections![fundingSourceId] =
              projection;
          }
        }
      } catch (error) {
        console.error(
          'Failed shadow policy claim projection; falling back to legacy calculations',
          {
            clientId: mutableClient.clientId,
            fundingSourceId,
            policyType: ltcPolicyForProjection.policyType,
            assertIdentities: shouldAssertProjectionIdentities,
            error,
          },
        );
      }

      // When the claim-projection engine is active, premiums run through the
      // OUT-OF-POCKET window until benefits begin: pass the projection's
      // months-from-issue-to-first-payout (anchored at the start of the issue
      // year) so `calcPolicyTotalCost` lays the premium schedule from issue to
      // exactly when benefits begin — past care onset (`ltcAtYear`), through the
      // pre-payout out-of-pocket months, stopping the month benefits start.
      // Undefined when the engine is off or the policy never pays — premiums then
      // fall back to stopping at care onset (legacy). Membership is handled inside
      // `calcPolicyTotalCost` (its dues run through care end regardless).
      const premiumPayThroughMonths =
        useClaimProjectionEngine && claimProjection
          ? claimProjection.terms.monthsFromIssueToFirstPayout
          : undefined;

      calcPolicyTotalCost(mutableClient, premiumPayThroughMonths);

      // Coverage step: when the claim-projection engine is on for this client
      // AND a projection succeeded, derive the legacy-shaped coverage outputs
      // from it; otherwise run the legacy coverage calc. Downstream ROI +
      // combined rollups consume whichever path's phaseCalculations/scalars.
      if (useClaimProjectionEngine && claimProjection) {
        projectionToLegacy(mutableClient, mutableClient.fundingSources.ltcPolicy, claimProjection, {
          clientHasStartedLtc,
          isJoint: projectionIsJoint,
        });
      } else {
        calcProjectedCostCoverageFn(mutableClient);
      }

      // calculate policy projected ROI
      const {
        policyProjectedReturnOnInvestment,
        policyProjectedCompoundAnnualGrowthRate,
        policyProjectedInternalRateOfReturn,
        isIrrSuspect: policyIsIrrSuspect,
        investmentPeriodYears,
        investments,
        returns,
      } = calcPolicyProjectedROI(
        mutableClient,
        mutableClient.fundingSources.ltcPolicy,
        discountRate ?? 0,
      );

      // test that total cash flows are equal to the policy total cost
      investmentCashflowEqualsPolicyTotalCost(
        investments,
        mutableClient.fundingSources.ltcPolicy.policyTotalCost!,
      );
      if (!allPhaseInflatedProfessionalShareCostDiscountAmount) {
        // only run this check if there is no discount applied
        // this is because when discounts are applied, the savings are added to the policy total value
        // but the return cash flows are not affected by the discount, so the check will fail
        returnCashflowEqualsPolicyTotalValue(
          returns,
          mutableClient.fundingSources.ltcPolicy.policyTotalValue!,
        );
      }

      const ltcPolicy = mutableClient.fundingSources.ltcPolicy;
      ltcPolicy.policyProjectedReturnOnInvestment = policyProjectedReturnOnInvestment;
      ltcPolicy.policyProjectedCompoundAnnualGrowthRate = policyProjectedCompoundAnnualGrowthRate;
      ltcPolicy.policyProjectedInternalRateOfReturn = policyProjectedInternalRateOfReturn;
      ltcPolicy.isIrrSuspect = policyIsIrrSuspect;
      ltcPolicy.investmentPeriodYears = investmentPeriodYears;
      ltcPolicy.investmentCashFlows = investments;
      ltcPolicy.returnCashFlows = returns;

      // update multipleFundingSources
      multipleFundingSources.ltcPolicy[fundingSourceId] = mutableClient.fundingSources.ltcPolicy;
      mutableClient.policyPhaseCalculations[fundingSourceId] = mutableClient.phaseCalculations;

      //if the policy being calculated is a joint policy, then update partner's multipleFundingSources and policyPhaseCalculations from the working slot
      if (isJointPolicy(policyFundingSource.policyType, mutableClientPartner)) {
        mutableClientPartner!.multipleFundingSources.ltcPolicy[fundingSourceId] =
          mutableClientPartner.fundingSources.ltcPolicy;
        if (!mutableClientPartner.policyPhaseCalculations) {
          mutableClientPartner.policyPhaseCalculations = {};
        }
        mutableClientPartner!.policyPhaseCalculations[fundingSourceId] =
          mutableClientPartner!.phaseCalculations;
      }
    },
  );

  calcMultiplePolicyTotalFields(mutableClient);
}

// Assemble the joint projection context for a ported joint policy: mirror the
// (clean, cloned) policy onto the partner so per-claimant reads (the rider's
// premium-start year, etc.) resolve against the shared policy, build the
// calendar-aligned merged stream, and turn its anchor year into the shared
// policy-year anchor. Returns undefined when there is no partner.
export function buildJointProjectionContext(
  mutableClient: Draft<Client>,
  ltcPolicy: PolicyFundingSource,
): JointProjectionContext | undefined {
  const partner = mutableClient.mutableClientPartner;
  if (!partner) return undefined;
  // An undefined premium start can't anchor the policy year. Fail loudly so the
  // caller's try/catch falls back to legacy — matching single-life, where
  // getCalendarPolicyYear → getPhasePolicyStartYear throws on the same gap
  // (rather than silently fabricating a current-year anchor for joint only).
  const premiumStart = ltcPolicy.policyPremiumStartYear;
  if (isNullOrUndefined(premiumStart)) {
    throw new Error('Joint projection requires policyPremiumStartYear');
  }
  // The legacy path re-clones later; this call is idempotent.
  copyJointPolicyToClientPartner(mutableClient);
  const built = buildJointMonthlyCareInputs(mutableClient);
  if (!built) return undefined;
  // Policy year at monthIndex 0. `buildJointMonthlyCareInputs` anchors monthIndex
  // 0 at Jan 1 of `floor(anchorYear)` (the earliest spouse's onset *year*), not
  // at the fractional onset itself — each spouse's offset is measured from that
  // same calendar origin (`round((startYear − floor(anchorYear)) × 12)`). So
  // flooring here matches: `floor(anchorYear) − premiumStart + 1` is the policy
  // year at that Jan-1 origin, and `resolveRowPolicyYear` adds `floor(monthIndex
  // /12)` to land every row on its true calendar policy year by integer math.
  // Anchoring at Jan 1 (rather than the fractional onset) is what makes
  // `floor(anchorYear) + floor(monthIndex/12)` equal `floor(rowCalendarYear)`:
  // measuring the offset from the fractional onset instead understated the later
  // spouse by a year whenever the inter-spouse gap didn't carry the year (e.g.
  // anchor 2024.58, premium 2020: a session client onset 2025 would land at
  // monthIndex 5 with `floor(5/12)=0`, pinning it to the anchor's year 5 instead
  // of its real year 6). Single-life is unaffected (it floors via
  // `getCalendarPolicyYear`), and integer-onset joint runs are bit-identical
  // (`floor(2024) === 2024`, so the offset origin is unchanged).
  //
  // Intentionally NOT clamped to >= 1 here. The anchor may be <= 0 when the
  // earliest-claiming spouse begins care before the policy's premium-start year
  // (e.g. an already-on-claim partner whose care predates this policy).
  // `resolveRowPolicyYear` adds the per-row calendar offset and returns the
  // result unclamped, so every row sits on one monotonic calendar clock; rows
  // that land < 1 are caught by each strategy's pre-policy gate (paid $0, full
  // cost out-of-pocket). Clamping the anchor instead baked a permanent +1 skew
  // into every later row's policy year.
  const anchorPolicyYear =
    built.anchorYear != null ? Math.floor(built.anchorYear) - premiumStart + 1 : 1;
  return {
    monthlyCareInputs: built.monthlyCareInputs,
    partnerClient: partner,
    anchorPolicyYear,
  };
}

function runSelfFundingCalculations(mutableClient: Draft<Client>) {
  const {
    intakeSurvey: { clientHasStartedLtc },
    multipleFundingSources,
    clientCalculationSettings: { discountRate },
  } = mutableClient;

  const calcFunctions = clientHasStartedLtc
    ? [calcSelfFundingTotalsOnClaim]
    : [calcSelfFundingProjectedCostCoverage, calcSelfFundingTotalCost];

  Object.entries(multipleFundingSources.selfFunding).forEach(
    ([fundingSourceId, selfFundingSource]) => {
      // initialize selfFunding
      mutableClient.fundingSources.selfFunding = cloneDeep(selfFundingSource);

      const { calcMethod } = getCalcConfig(selfFundingSource.accountType);

      if (calcMethod === 'income-stream') {
        // Income streams (e.g. retirement income) use a non-compounding calculation.
        calcIncomeStreamProjectedCoverage(mutableClient);
      } else {
        // run standard investment-based calculations
        calcFunctions.forEach(calc => calc(mutableClient));

        // calculate self-funding projected ROI (not applicable to income streams)
        const { investmentPeriodYears } = calcSelfFundingProjectedROI(
          mutableClient,
          discountRate ?? undefined,
        );

        const selfFunding = mutableClient.fundingSources.selfFunding;
        selfFunding.investmentPeriodYears = investmentPeriodYears;
      }

      // update multipleFundingSources
      multipleFundingSources.selfFunding[fundingSourceId] =
        mutableClient.fundingSources.selfFunding;
    },
  );

  calcMultipleSelfFundingTotalFields(mutableClient);
}

function calcSourceDefaults(mutableClient: Draft<Client>) {
  const {
    appliedInferenceSet: { ltcAtYear },
    multipleFundingSources,
  } = mutableClient;

  const currentYear = new Date().getFullYear();
  Object.values(multipleFundingSources.selfFunding).forEach(selfFunding => {
    const sf = selfFunding as SelfFundingSource;

    calcDetailDefaults(selfFunding as NumberInputSelfFundingSource, {
      contributionStartYear: currentYear,
      contributionEndYear: ltcAtYear,
    });
    // Always sync contribution start year
    sf.contributionStartYear = currentYear;
    // Only sync contribution end year to ltcAtYear when no custom value is set.
    // When a custom value is set, calcSelfFundingProjectedCostCoverage will resolve it.
    if (sf.contributionStopYear == null) {
      sf.contributionEndYear = ltcAtYear;
    }
  });
}

function calcDetailDefaults<T extends {}>(source: T, defaults: Partial<T>) {
  (Object.entries(defaults) as [keyof T, number][]).forEach(
    ([key, defaultValue]) => (source[key] = (source[key] ?? defaultValue) as any),
  );
}

function getKeyByValue(enumObj: any, value: number): string | undefined {
  for (const key in enumObj) {
    if (enumObj[key] === value) {
      return key;
    }
  }
  return undefined; // Return undefined if no match is found
}

function capitalizeFirstLetter(inputString: string): string {
  if (inputString.length === 0) {
    return inputString; // Return the input string as is if it's empty
  }

  const firstLetter = inputString.charAt(0).toUpperCase();
  const restOfString = inputString.slice(1);

  return firstLetter + restOfString;
}

type PolicyCoverage = {
  portionOfPhaseCostBenefitEligible: number;
  maxPayoutAccordingToDailyMaximum?: number;
  phaseMonthlyCost: number;
  phaseCostPerDayNeeded: number;
  phaseCareDaysNeeded: number;
  daysWhereLessThanTwoADLs?: number;
  applicableWaitingPeriodDays?: number;
  phaseBenefitEligibleCareDays?: number;
  phaseEligibleCareDaysAfter2AdlRequirement?: number;
  phaseEligibleCareDaysAfterWaitingPeriod?: number;
  dailyBenefitAmount?: number;
  inflationProtection?: number | null;
  inflationPeriodYears?: number;
  inflatedDailyBenefitAmount?: number;
  inflatedDailyBenefitAmountBase?: number;
  inflatedDailyBenefitAmountRider?: number;
  inflatedFaceAmount?: number;
  dailyBenefitAmountBase?: number;
  baseBenefitAmountRemaining?: number;
  baseAmountApplicableDays?: number;
  riderAmountApplicableDays?: number;
};

export function calcSinglePhasePolicyCoverage(
  mutableClient: Draft<Client>,
  appliedCareEnvironment: CareEnvironment,
  phaseCosts: SinglePhaseCosts,
  policyDataAccumulator: PolicyDataAccumulator | JointPolicyDataAccumulator,
): PolicyCoverage {
  const {
    fundingSources: {
      ltcPolicy,
      ltcPolicy: {
        policyInflationProtection,
        policyPremiumStartYear,
        policyInflationProtectionYears,
      },
    },
    clientPhasePredictedEndYears,
  } = mutableClient;

  const isCalculationTypeHybridLifeIra = checkPolicyCalculationTypeEquals(
    ltcPolicy.policyType,
    FundingPolicyType.hybridLifeIra,
  );

  const { phaseProfessionalShareCost } = phaseCosts;

  const {
    phaseEligibleCareDays: phaseBenefitEligibleCareDays,
    daysWhereLessThanTwoADLs,
    applicableWaitingPeriodDays,
    phaseEligibleCareDaysAfter2AdlRequirement,
    phaseEligibleCareDaysAfterWaitingPeriod,
  } = handlePhaseBenefitEligibleCareDays(mutableClient, phaseCosts);
  const {
    phaseCareDaysNeeded,
    phaseMonthlyCost,
    phaseCostPerDayNeeded,
    portionOfPhaseCostBenefitEligible,
  } = derivePhaseCostMetrics(phaseCosts, phaseBenefitEligibleCareDays);

  if (phaseProfessionalShareCost === 0) {
    return {
      portionOfPhaseCostBenefitEligible: 0,
      phaseCareDaysNeeded,
      phaseMonthlyCost,
      phaseCostPerDayNeeded,
      daysWhereLessThanTwoADLs,
      applicableWaitingPeriodDays,
      phaseEligibleCareDaysAfter2AdlRequirement,
      phaseEligibleCareDaysAfterWaitingPeriod,
    };
  }
  // Calculate daily benefit amount and inflation protection
  const dailyBenefitAmountVar = careEnvironmentToDailyMaxKey[appliedCareEnvironment];
  let dailyBenefitAmount = ltcPolicy[dailyBenefitAmountVar] as number | null | undefined;

  // Reimbursement policies do not cover Independent Living. Zero out the
  // benefit for shop-insurance policies so phase coverage and downstream
  // totals reflect the product reality. Manually-added policies respect
  // whatever the advisor entered.
  if (
    appliedCareEnvironment === CareEnvironment.independentLiving &&
    isIndependentLivingBenefitZeroedOut(ltcPolicy)
  ) {
    dailyBenefitAmount = 0;
  }

  if (dailyBenefitAmount === 0) {
    return {
      portionOfPhaseCostBenefitEligible: 0,
      phaseCareDaysNeeded,
      phaseMonthlyCost,
      phaseCostPerDayNeeded,
      daysWhereLessThanTwoADLs,
      applicableWaitingPeriodDays,
      phaseEligibleCareDaysAfter2AdlRequirement,
      phaseEligibleCareDaysAfterWaitingPeriod,
    };
  }

  const policyPurchaseToPhaseEndYears = Math.max(
    clientPhasePredictedEndYears[phaseCosts.carePhase]! - policyPremiumStartYear!,
    0,
  );
  const inflationPeriodYears = Math.min(
    policyPurchaseToPhaseEndYears,
    policyInflationProtectionYears ?? Infinity,
  );

  // no daily max benefit amount, return portion of phase cost that is benefit eligible
  if (isNullOrUndefined(dailyBenefitAmount) && !isCalculationTypeHybridLifeIra) {
    return {
      portionOfPhaseCostBenefitEligible,
      phaseCareDaysNeeded,
      phaseMonthlyCost,
      inflationPeriodYears,
      phaseCostPerDayNeeded,
      daysWhereLessThanTwoADLs,
      applicableWaitingPeriodDays,
      phaseEligibleCareDaysAfter2AdlRequirement,
      phaseEligibleCareDaysAfterWaitingPeriod,
    };
  }

  // Calculate inflated daily max benefit amount
  const careEnvironmentInflationProtectionVar =
    careEnvironmentToInflationProtectionKeys[appliedCareEnvironment];

  const careEnvInflationValue = ltcPolicy[careEnvironmentInflationProtectionVar];
  const inflationProtection =
    typeof careEnvInflationValue === 'number' && careEnvInflationValue
      ? careEnvInflationValue
      : policyInflationProtection;

  const {
    maxPayoutAccordingToDailyMaximum,
    dailyBenefitAmountBase,
    baseBenefitAmountRemaining,
    baseAmountApplicableDays,
    riderAmountApplicableDays,
    inflatedDailyBenefitAmount,
    inflatedDailyBenefitAmountBase,
    inflatedDailyBenefitAmountRider,
    inflatedFaceAmount,
  } = calcMaxPayOutAccordingToDailyMaximum(
    mutableClient,
    ltcPolicy,
    policyDataAccumulator,
    phaseBenefitEligibleCareDays,
    dailyBenefitAmount as number,
    inflationPeriodYears,
    phaseCostPerDayNeeded,
  );

  return {
    maxPayoutAccordingToDailyMaximum,
    portionOfPhaseCostBenefitEligible,
    phaseMonthlyCost,
    phaseCostPerDayNeeded,
    phaseCareDaysNeeded,
    phaseBenefitEligibleCareDays,
    daysWhereLessThanTwoADLs,
    applicableWaitingPeriodDays,
    phaseEligibleCareDaysAfter2AdlRequirement,
    phaseEligibleCareDaysAfterWaitingPeriod,
    inflationProtection,
    inflationPeriodYears,
    inflatedDailyBenefitAmount,
    inflatedDailyBenefitAmountBase,
    inflatedDailyBenefitAmountRider,
    inflatedFaceAmount,
    dailyBenefitAmount: dailyBenefitAmount ?? undefined,
    dailyBenefitAmountBase,
    baseBenefitAmountRemaining,
    baseAmountApplicableDays,
    riderAmountApplicableDays,
  };
}

function calcMaxPayOutAccordingToDailyMaximum(
  mutableClient: Draft<Client>,
  ltcPolicy: PolicyFundingSource,
  policyDataAccumulator: PolicyDataAccumulator,
  phaseBenefitEligibleCareDays: number,
  dailyBenefitAmount: number,
  inflationPeriodYears: number,
  phaseCostPerDayNeeded: number,
) {
  const {
    policyMaximumBenefitAmount,
    policyInflationProtection,
    policyInflationProtectionOnCOBAmount,
    policyInflationProtectionOnFaceAmount,
    policyMonthlyWithdrawalRate,
    isIndemnityPolicyPayment,
  } = ltcPolicy;
  const isCalculationTypeHybridLifeIra = checkPolicyCalculationTypeEquals(
    ltcPolicy.policyType,
    FundingPolicyType.hybridLifeIra,
  );

  let maxPayoutAccordingToDailyMaximum;
  let baseAmountApplicableDays;
  let riderAmountApplicableDays;
  let dailyBenefitAmountBase;

  const deathBenefitAmount = policyMaximumBenefitAmount ?? 0;
  const totalProjectedCostCoverage = isJointPolicyDataAccumulator(policyDataAccumulator)
    ? policyDataAccumulator.projectedCostCoverage +
      policyDataAccumulator.partnerProjectedCostCoverage
    : policyDataAccumulator.projectedCostCoverage;

  const calculateInflatedAmount = (inflationProtection: number | null) =>
    calcInflationProtectionFutureValue(
      mutableClient,
      dailyBenefitAmount ?? 0,
      inflationPeriodYears,
      inflationProtection ?? 0,
    );

  const inflatedDailyBenefitAmounts = {
    policyInflationProtection: calculateInflatedAmount(policyInflationProtection),
    policyInflationProtectionOnFaceAmount: calculateInflatedAmount(
      policyInflationProtectionOnFaceAmount ?? policyInflationProtection,
    ),
    policyInflationProtectionOnCOBAmount: calculateInflatedAmount(
      policyInflationProtectionOnCOBAmount ?? policyInflationProtection,
    ),
  };

  const inflatedFaceAmount = calcInflationProtectionFutureValue(
    mutableClient,
    deathBenefitAmount,
    inflationPeriodYears,
    policyInflationProtectionOnFaceAmount ?? policyInflationProtection ?? 0,
  );

  const baseBenefitAmountRemaining = Math.max(inflatedFaceAmount - totalProjectedCostCoverage, 0);

  if (isCalculationTypeHybridLifeIra) {
    const monthlyWithDrawalRate = policyMonthlyWithdrawalRate ?? 0;

    // Cap dailyBenefitAmountBase by phaseCostPerDayNeeded
    const uncappedDailyBenefitAmountBase = (inflatedFaceAmount * monthlyWithDrawalRate) / 30;
    dailyBenefitAmountBase = isIndemnityPolicyPayment
      ? uncappedDailyBenefitAmountBase
      : Math.min(uncappedDailyBenefitAmountBase, phaseCostPerDayNeeded);
    ltcPolicy.dailyBenefitAmountBase = dailyBenefitAmountBase;

    baseAmountApplicableDays = Math.min(
      baseBenefitAmountRemaining / dailyBenefitAmountBase,
      phaseBenefitEligibleCareDays,
    );

    riderAmountApplicableDays = phaseBenefitEligibleCareDays - baseAmountApplicableDays;

    // Cap rider daily benefit amount by phaseCostPerDayNeeded
    const uncappedRiderDailyAmount = policyInflationProtectionOnCOBAmount
      ? inflatedDailyBenefitAmounts.policyInflationProtectionOnCOBAmount
      : inflatedDailyBenefitAmounts.policyInflationProtection;
    const cappedRiderDailyAmount = isIndemnityPolicyPayment
      ? uncappedRiderDailyAmount
      : Math.min(uncappedRiderDailyAmount, phaseCostPerDayNeeded);

    const riderMaximumPayOut = riderAmountApplicableDays * cappedRiderDailyAmount;

    maxPayoutAccordingToDailyMaximum =
      baseAmountApplicableDays * dailyBenefitAmountBase + riderMaximumPayOut;

    return {
      maxPayoutAccordingToDailyMaximum,
      dailyBenefitAmountBase,
      baseBenefitAmountRemaining,
      baseAmountApplicableDays,
      inflatedFaceAmount,
      riderAmountApplicableDays,
      inflatedDailyBenefitAmount: inflatedDailyBenefitAmounts.policyInflationProtection,
      inflatedDailyBenefitAmountBase:
        inflatedDailyBenefitAmounts.policyInflationProtectionOnFaceAmount,
      inflatedDailyBenefitAmountRider:
        inflatedDailyBenefitAmounts.policyInflationProtectionOnCOBAmount,
    };
  }

  if (isHybridPolicy(ltcPolicy.policyType)) {
    dailyBenefitAmountBase = dailyBenefitAmount;

    // Cap base daily benefit amount by phaseCostPerDayNeeded
    const uncappedDailyBenefitAmountBase =
      inflatedDailyBenefitAmounts.policyInflationProtectionOnFaceAmount;
    const cappedDailyBenefitAmountBase = isIndemnityPolicyPayment
      ? uncappedDailyBenefitAmountBase
      : Math.min(uncappedDailyBenefitAmountBase, phaseCostPerDayNeeded);

    // Cap rider daily benefit amount by phaseCostPerDayNeeded
    const uncappedDailyBenefitAmountRider =
      inflatedDailyBenefitAmounts.policyInflationProtectionOnCOBAmount;
    const cappedDailyBenefitAmountRider = isIndemnityPolicyPayment
      ? uncappedDailyBenefitAmountRider
      : Math.min(uncappedDailyBenefitAmountRider, phaseCostPerDayNeeded);

    baseAmountApplicableDays = Math.min(
      baseBenefitAmountRemaining / cappedDailyBenefitAmountBase,
      phaseBenefitEligibleCareDays,
    );
    riderAmountApplicableDays = phaseBenefitEligibleCareDays - baseAmountApplicableDays;

    maxPayoutAccordingToDailyMaximum =
      baseAmountApplicableDays * cappedDailyBenefitAmountBase +
      riderAmountApplicableDays * cappedDailyBenefitAmountRider;

    return {
      maxPayoutAccordingToDailyMaximum,
      dailyBenefitAmountBase,
      baseBenefitAmountRemaining,
      baseAmountApplicableDays,
      riderAmountApplicableDays,
      inflatedDailyBenefitAmount: inflatedDailyBenefitAmounts.policyInflationProtection,
      inflatedDailyBenefitAmountBase: uncappedDailyBenefitAmountBase,
      inflatedDailyBenefitAmountRider: uncappedDailyBenefitAmountRider,
    };
  }

  maxPayoutAccordingToDailyMaximum =
    inflatedDailyBenefitAmounts.policyInflationProtection * phaseBenefitEligibleCareDays;

  return {
    maxPayoutAccordingToDailyMaximum,
    inflatedDailyBenefitAmount: inflatedDailyBenefitAmounts.policyInflationProtection,
  };
}

function handlePhaseBenefitEligibleCareDays(
  mutableClient: Draft<Client>,
  phaseCosts: SinglePhaseCosts,
) {
  const {
    intakeSurvey: { clientAdlReceivedAssistanceCount },
    fundingSources: {
      ltcPolicy: { policyBenefitPeriodMonthsRemaining, policyType },
      ltcPolicy,
    },
  } = mutableClient;
  const { phaseCareMonthsNeeded } = phaseCosts;

  const daysInMonth = 30;
  const oneAdlDurationMultiplier = 0.5;
  const phaseCareDaysNeeded = phaseCareMonthsNeeded * daysInMonth;

  if (isMembershipProgram(policyType)) {
    return {
      phaseEligibleCareDays: phaseCareDaysNeeded,
      daysWhereLessThanTwoADLs: 0,
      applicableWaitingPeriodDays: 0,
      phaseEligibleCareDaysAfter2AdlRequirement: phaseCareDaysNeeded,
      phaseEligibleCareDaysAfterWaitingPeriod: phaseCareDaysNeeded,
    };
  }

  const policyBenefitPeriodDaysRemaining = (policyBenefitPeriodMonthsRemaining ?? 0) * daysInMonth;

  const policyActivatesAtADLCount = 2;

  const daysWhereLessThanTwoADLs =
    phaseCosts.carePhase === CarePhase.earlyCare &&
    clientAdlReceivedAssistanceCount < policyActivatesAtADLCount
      ? phaseCareDaysNeeded * oneAdlDurationMultiplier
      : 0;

  const phaseEligibleCareDaysAfter2AdlRequirement = phaseCareDaysNeeded - daysWhereLessThanTwoADLs;

  const applicableWaitingPeriodDays = calcApplicableWaitingPeriodDaysAndDecrement(
    mutableClient,
    phaseCosts,
    phaseEligibleCareDaysAfter2AdlRequirement,
  );

  if (checkPolicyCalculationTypeEquals(policyType, FundingPolicyType.shortTermCareInsurance)) {
    const phaseEligibleCareDays = Math.min(
      phaseCareDaysNeeded - (daysWhereLessThanTwoADLs + applicableWaitingPeriodDays),
      policyBenefitPeriodDaysRemaining,
    );
    ltcPolicy.policyBenefitPeriodMonthsRemaining! -= phaseEligibleCareDays / daysInMonth;

    const phaseEligibleCareDaysAfterWaitingPeriod = Math.min(
      phaseEligibleCareDays,
      phaseEligibleCareDaysAfter2AdlRequirement - applicableWaitingPeriodDays,
    );
    return {
      phaseEligibleCareDays,
      daysWhereLessThanTwoADLs,
      applicableWaitingPeriodDays,
      phaseEligibleCareDaysAfter2AdlRequirement,
      phaseEligibleCareDaysAfterWaitingPeriod,
    };
  }
  const phaseEligibleCareDays =
    phaseCareDaysNeeded - (daysWhereLessThanTwoADLs + applicableWaitingPeriodDays);

  const phaseEligibleCareDaysAfterWaitingPeriod =
    phaseEligibleCareDaysAfter2AdlRequirement - applicableWaitingPeriodDays;

  return {
    phaseEligibleCareDays,
    daysWhereLessThanTwoADLs,
    applicableWaitingPeriodDays,
    phaseEligibleCareDaysAfter2AdlRequirement,
    phaseEligibleCareDaysAfterWaitingPeriod,
  };
}

function calcApplicableWaitingPeriodDaysAndDecrement(
  client: Draft<Client>,
  phaseCost: SinglePhaseCosts,
  phaseDaysNeeded: number,
): number {
  const {
    fundingSources: {
      ltcPolicy,
      ltcPolicy: { homeCareWaitingPeriodDaysRemaining, facilityCareWaitingPeriodDaysRemaining },
    },
    careEnvironmentSelections,
    appliedCareEnvironments,
  } = client;

  const selectedCareSetting = careEnvironmentSelections?.[phaseCost.carePhase]
    ? careEnvironmentSelections[phaseCost.carePhase]
    : appliedCareEnvironments[phaseCost.carePhase];

  // calculate cost of waiting period based on care setting and corresponding waiting period
  if (selectedCareSetting === CareEnvironment.home && homeCareWaitingPeriodDaysRemaining) {
    const applicableWaitingDays = Math.min(homeCareWaitingPeriodDaysRemaining, phaseDaysNeeded);

    ltcPolicy.homeCareWaitingPeriodDaysRemaining! -= applicableWaitingDays;
    // Decrement remaining waiting period days for facility as well. Waiting periods are not independent
    if (facilityCareWaitingPeriodDaysRemaining) {
      ltcPolicy.facilityCareWaitingPeriodDaysRemaining = Math.max(
        0,
        facilityCareWaitingPeriodDaysRemaining - applicableWaitingDays,
      );
    }
    return applicableWaitingDays;
  }

  if (selectedCareSetting !== CareEnvironment.home && facilityCareWaitingPeriodDaysRemaining) {
    const applicableWaitingDays = Math.min(facilityCareWaitingPeriodDaysRemaining, phaseDaysNeeded);
    ltcPolicy.facilityCareWaitingPeriodDaysRemaining! -= applicableWaitingDays;
    // Decrement remaining waiting period days for home as well. Waiting periods are not independent
    if (homeCareWaitingPeriodDaysRemaining) {
      ltcPolicy.homeCareWaitingPeriodDaysRemaining = Math.max(
        0,
        homeCareWaitingPeriodDaysRemaining - applicableWaitingDays,
      );
    }
    return applicableWaitingDays;
  }
  return 0;
}

export function setCoverageToUndefined<T extends keyof CalculatedPolicyFundingSource>(
  mutableClient: Draft<Client>,
  coverageType: T,
): void {
  mutableClient.fundingSources.ltcPolicy[coverageType] = undefined;
}

export function getAppliedCareEnvironmentKeys(mutableClient: Draft<Client>): string[] {
  const { appliedCareEnvironments } = mutableClient;

  return Object.values(appliedCareEnvironments)
    .map(value => getKeyByValue(CareEnvironment, value))
    .filter(key => key !== undefined) // Filter out undefined values
    .map(key => capitalizeFirstLetter(key!)); // Use non-null assertion operator
}

function sumPropertyForAllPolicies(
  property: keyof PolicyFundingSource,
  ltcPolicies: WritableDraft<PolicyFundingSource>[],
): number | undefined {
  // check if all values are undefined
  if (ltcPolicies.every(policy => policy[property] === undefined)) {
    return undefined;
  }
  return ltcPolicies.reduce((acc, policy) => {
    const value = policy[property];
    return acc + (typeof value === 'number' ? value : 0);
  }, 0);
}

export function calcCombinedFundingTotalCost(mutableClient: Draft<Client>) {
  const {
    fundingSources,
    fundingSources: {
      annuity: { annuityTotalCost, isDeactivated: isAnnuityDeactivated },
    },
    multipleFundingSources: { multiplePolicyTotalCost, multipleSelfFundingTotalCost },
  } = mutableClient;

  if (
    isNullOrUndefined(multipleSelfFundingTotalCost) &&
    isNullOrUndefined(multiplePolicyTotalCost) &&
    isNullOrUndefined(annuityTotalCost)
  ) {
    fundingSources.combinedFundingTotalCost = undefined;
    return;
  }

  fundingSources.combinedFundingTotalCost =
    (multipleSelfFundingTotalCost ?? 0) +
    (multiplePolicyTotalCost ?? 0) +
    (isAnnuityDeactivated ? 0 : (annuityTotalCost ?? 0));
}

function calcCombinedNonLtcPayout(mutableClient: Draft<Client>) {
  const {
    fundingSources,
    fundingSources: {
      annuity: { annuityNonLtcPayout, isDeactivated: isAnnuityDeactivated },
    },
    multipleFundingSources: { multiplePolicyNonLtcPayout, multipleSelfFundingNonLtcPayout },
  } = mutableClient;

  fundingSources.combinedNonLtcPayout =
    (multipleSelfFundingNonLtcPayout ?? 0) +
    (isAnnuityDeactivated ? 0 : (annuityNonLtcPayout ?? 0)) +
    (multiplePolicyNonLtcPayout ?? 0);
}

export function roundDownToNearestHundredth(value: number): number {
  return Math.floor(value * 100) / 100;
}

export function calcCombinedProjectedCostCoverage(mutableClient: Draft<Client>) {
  const {
    allPhaseCosts: { allPhaseInflatedProfessionalShareCost },
    fundingSources,
    fundingSources: {
      annuity: { annuityProjectedCostCoverage, isDeactivated: isAnnuityDeactivated },
    },
    multipleFundingSources: {
      multiplePolicyProjectedCostCoverage,
      multipleSelfFundingProjectedCostCoverage,
    },
  } = mutableClient;

  if (
    isNullOrUndefined(multipleSelfFundingProjectedCostCoverage) &&
    isNullOrUndefined(multiplePolicyProjectedCostCoverage) &&
    isNullOrUndefined(allPhaseInflatedProfessionalShareCost)
  ) {
    fundingSources.combinedProjectedCostCoverage = undefined;
    fundingSources.combinedProjectedCostCoveragePercent = undefined;
    return;
  }

  fundingSources.combinedProjectedCostCoverage =
    (multipleSelfFundingProjectedCostCoverage ?? 0) +
    (multiplePolicyProjectedCostCoverage ?? 0) +
    (isAnnuityDeactivated ? 0 : (annuityProjectedCostCoverage ?? 0));

  fundingSources.combinedProjectedCostCoveragePercent =
    allPhaseInflatedProfessionalShareCost > 0
      ? roundDownToNearestHundredth(
          (fundingSources.combinedProjectedCostCoverage ?? 0) /
            allPhaseInflatedProfessionalShareCost,
        )
      : 1;
}

export function calcPolicyInflatedMaxBenefitToEndOfPhase(
  client: Client,
  carePhase: CarePhase,
  policyPremiumStartYear: number,
  policyMaximumBenefitAmount: number,
  inflationRate?: number,
  policyInflationProtectionYears?: number | null,
) {
  const {
    clientPhasePredictedEndYears,
    fundingSources: {},
  } = client;

  if (isCarePhasePast(carePhase, client)) {
    return policyMaximumBenefitAmount;
  }

  let inflationPeriod = Math.max(
    clientPhasePredictedEndYears[carePhase]! - policyPremiumStartYear,
    0,
  );
  // Limit inflation period by policyInflationProtectionYears if it exists
  if (!isNullOrUndefined(policyInflationProtectionYears)) {
    inflationPeriod = Math.min(inflationPeriod, policyInflationProtectionYears);
  }

  client.fundingSources.ltcPolicy.policyInflationPeriodYears = inflationPeriod;
  return calcInflationProtectionFutureValue(
    client,
    policyMaximumBenefitAmount,
    inflationPeriod,
    inflationRate,
  );
}

function calcInflationProtectionFutureValue(
  client: Client,
  principle: number,
  inflationPeriodYears: number,
  inflationRate?: number,
) {
  const {
    fundingSources: {
      ltcPolicy: { policyInflationProtection, isSimpleInflationProtection },
    },
  } = client;
  const effectiveInflationRate = inflationRate ?? policyInflationProtection;
  return isSimpleInflationProtection
    ? calcFutureValueSimpleInflation(principle, effectiveInflationRate ?? 0, inflationPeriodYears)
    : calcFutureValue(principle, effectiveInflationRate ?? 0, inflationPeriodYears);
}

export function calcPolicyCoverageImpl(mutableClient: Draft<Client>, coverageType: string): void {
  const {
    fundingSources: {
      ltcPolicy: { policyType },
      ltcPolicy,
    },
    phaseCosts,
    mutableClientPartner,
  } = mutableClient;

  // CareMatters II / Together: seed base-pool consumption from any
  // pre-claim utilization reported on the policy. Treated as base-only
  // dollars paid (rider-vs-base split is not separately tracked on
  // illustrations today). See plan open item 4.
  const initialBaseDollarsPaid = shouldUseCareMattersCalc(ltcPolicy)
    ? (ltcPolicy.policyBenefitUtilizedToDate ?? 0)
    : 0;

  let policyDataAccumulator: PolicyDataAccumulator | JointPolicyDataAccumulator = {
    indemnitySurplus: 0,
    phaseCalculations: [],
    projectedCostCoverage: 0,
    cumulativePolicyPayOut: 0, // may be different than projected cost coverage for indemnity policies
    benefitPoolsExhausted: {},
    sharedPoolUtilized: 0,
    baseDollarsPaid: initialBaseDollarsPaid,
    riderDollarsPaid: 0,
  };

  if (isJointPolicy(policyType!, mutableClientPartner) && !ltcPolicy.isDeactivated) {
    copyJointPolicyToClientPartner(mutableClient);
    const combinedSortedPhaseCosts = combineSortClientPartnerPhaseCosts(mutableClient);

    policyDataAccumulator = {
      ...policyDataAccumulator,
      partnerPhaseCalculations: [],
      partnerProjectedCostCoverage: 0,
    };

    combinedSortedPhaseCosts.forEach(clientSinglePhaseCost => {
      processSinglePhase<JointPolicyDataAccumulator>(
        mutableClient.clientId,
        clientSinglePhaseCost.client,
        policyDataAccumulator as JointPolicyDataAccumulator,
        clientSinglePhaseCost.phaseCosts,
      );
    });
  } else {
    phaseCosts.forEach(singePhaseCosts => {
      processSinglePhase<PolicyDataAccumulator>(
        mutableClient.clientId,
        mutableClient,
        policyDataAccumulator,
        singePhaseCosts,
      );
    });
  }

  calcPolicySummaryStats(mutableClient, policyDataAccumulator);

  ltcPolicy[coverageType] = policyDataAccumulator.projectedCostCoverage;
  const lastPhaseCalculations =
    policyDataAccumulator.phaseCalculations[policyDataAccumulator.phaseCalculations.length - 1];
  ltcPolicy.policyRemainingBenefitAmount =
    lastPhaseCalculations.remainingMaxBenefitAfterPhaseReduction ?? 0;
  ltcPolicy.indemnitySurplus = policyDataAccumulator.indemnitySurplus;
  if (shouldUseCareMattersCalc(ltcPolicy)) {
    ltcPolicy.baseDollarsPaid = policyDataAccumulator.baseDollarsPaid;
    ltcPolicy.riderDollarsPaid = policyDataAccumulator.riderDollarsPaid;
  }
  mutableClient.phaseCalculations = policyDataAccumulator.phaseCalculations;

  if (
    isJointPolicy(policyType!, mutableClientPartner) &&
    isJointPolicyDataAccumulator(policyDataAccumulator)
  ) {
    mutableClientPartner.phaseCalculations = policyDataAccumulator.partnerPhaseCalculations;
    mutableClientPartner.fundingSources.ltcPolicy[coverageType] =
      policyDataAccumulator.partnerProjectedCostCoverage;
    mutableClientPartner.fundingSources.ltcPolicy.policyRemainingBenefitAmount =
      policyDataAccumulator.partnerPhaseCalculations[
        policyDataAccumulator.partnerPhaseCalculations.length - 1
      ]?.remainingMaxBenefitAfterPhaseReduction ?? 0;
  }
}

export function isJointPolicy(
  policyType: FundingPolicyType | null,
  mutableClientPartner: Client | null,
): mutableClientPartner is NonNullable<Client> {
  if (!policyType || !mutableClientPartner) {
    return false;
  }
  return fundingPolicyTypeDefs[policyType].isJoint === true;
}

type BenefitPoolKey =
  | 'totalBenefitAmount'
  | 'clientIndividualPoolAmount'
  | 'partnerIndividualPoolAmount'
  | 'sharedPoolAmount';
type PolicyDataAccumulatorBase = {
  indemnitySurplus: number;
  phaseCalculations: SinglePhaseCalculations[];
  projectedCostCoverage: number;
  cumulativePolicyPayOut: number;
  benefitPoolsExhausted: {
    [key in BenefitPoolKey]?: boolean;
  };
  // CareMatters II / Together only: dollars consumed from the flat base pool
  // (depletes pool, reduces death benefit) vs. dollars paid by the inflation
  // rider (does not deplete pool or reduce DB). Default 0; only written by
  // the CM II path. Joint policies share a single combined total — the joint
  // DB math doesn't need a per-insured split.
  baseDollarsPaid: number;
  riderDollarsPaid: number;
};

type PolicyDataAccumulator = PolicyDataAccumulatorBase;

type JointPolicyDataAccumulator = PolicyDataAccumulatorBase & {
  partnerPhaseCalculations: SinglePhaseCalculations[];
  partnerProjectedCostCoverage: number;
  sharedPoolUtilized: number;
};

function isJointPolicyDataAccumulator(
  accumulator: PolicyDataAccumulatorBase,
): accumulator is JointPolicyDataAccumulator {
  return (accumulator as JointPolicyDataAccumulator).partnerPhaseCalculations !== undefined;
}

function calcRemainingBenefitForShareCareRiderPlans(
  inflatedIndividualPoolAmount: number,
  policyDataAccumulator: JointPolicyDataAccumulator | PolicyDataAccumulatorBase,
  isClientSessionClient: boolean,
  policySharedBenefitPoolRemaining: number,
) {
  const { projectedCostCoverage, benefitPoolsExhausted } = policyDataAccumulator;

  // Get partner coverage amount if joint policy, otherwise 0
  const partnerProjectedCostCoverage = isJointPolicyDataAccumulator(policyDataAccumulator)
    ? policyDataAccumulator.partnerProjectedCostCoverage
    : 0;

  // Determine if individual pool is exhausted based on client type
  const isIndividualPoolExhausted = isClientSessionClient
    ? benefitPoolsExhausted.clientIndividualPoolAmount
    : benefitPoolsExhausted.partnerIndividualPoolAmount;

  // Get coverage amount based on client type
  const coverageAmount = isClientSessionClient
    ? projectedCostCoverage
    : partnerProjectedCostCoverage;

  // Calculate remaining individual pool amount
  const individualPoolRemaining = isIndividualPoolExhausted
    ? 0
    : Math.max(inflatedIndividualPoolAmount - coverageAmount, 0);

  // Return total remaining benefit including shared pool
  return Math.max(individualPoolRemaining + policySharedBenefitPoolRemaining, 0);
}

function processSinglePhase<AccumlatorType extends PolicyDataAccumulatorBase>(
  sessionClientId: string,
  mutableClient: Draft<Client>,
  policyDataAccumulator: AccumlatorType,
  phaseCosts: SinglePhaseCosts,
): void {
  // CareMatters II / Together: dedicated calc path. The flat-base-pool +
  // per-policy-year-rider mechanics don't fit the COB-pool model used by
  // the rest of the hybrid calc, so by-construction isolation here keeps
  // non-CM products free of regression risk. Legacy CM rows lacking the
  // benefit-period data fall through to the hybrid path below.
  if (shouldUseCareMattersCalc(mutableClient.fundingSources.ltcPolicy)) {
    processSinglePhaseCareMatters(
      sessionClientId,
      mutableClient,
      policyDataAccumulator,
      phaseCosts,
    );
    return;
  }

  const {
    fundingSources: {
      ltcPolicy: {
        isIndemnityPolicyPayment,
        policyPremiumStartYear,
        policyType,
        policyMaximumBenefitAmount,
        policyInflationProtection,
        policyBenefitUtilizedToDate,
      },
      ltcPolicy,
    },
  } = mutableClient;

  const policyTypeHasSharedPool =
    (policyType === FundingPolicyType.nylJointSecureCare ||
      policyType === FundingPolicyType.jointNglEssentialLtc) &&
    isJointPolicyDataAccumulator(policyDataAccumulator);

  if (isNullOrUndefined(policyPremiumStartYear)) {
    throw new Error('Policy premium start year is not defined');
  }

  const {
    isClientSessionClient,
    phaseIndex,
    phaseHomeCareWaitingPeriodDaysRemaining,
    phaseFacilityCareWaitingPeriodDaysRemaining,
    indemnitySurplusBeforePhase: indemnitySurplus,
    cumulativePolicyPayOutBefore: cumulativePolicyPayOut,
  } = derivePhaseContext(sessionClientId, mutableClient, policyDataAccumulator, phaseCosts);

  let inflatedMaxBenefitAmount;
  let remainingMaxBenefitBeforePhaseReduction;

  if (policyDataAccumulator.benefitPoolsExhausted.totalBenefitAmount) {
    // If benefit pool is exhausted
    inflatedMaxBenefitAmount = 0;
    remainingMaxBenefitBeforePhaseReduction = 0;

    const previousPhase =
      phaseCosts.carePhase === CarePhase.earlyCare ? CarePhase.earlyCare : phaseCosts.carePhase - 1;
    ltcPolicy.policyTotalLTCBenefit = calcPolicyTotalLtcLimitForPhase(
      mutableClient,
      previousPhase,
      unlimitedMaxBenefitMagicNumber,
      policyPremiumStartYear,
    );
  } else {
    inflatedMaxBenefitAmount = calcPolicyTotalLtcLimitForPhase(
      mutableClient,
      phaseCosts.carePhase,
      unlimitedMaxBenefitMagicNumber,
      policyPremiumStartYear,
    );

    if (phaseCosts.carePhase === CarePhase.fullCare) {
      ltcPolicy.policyTotalLTCBenefit = inflatedMaxBenefitAmount;
    }

    remainingMaxBenefitBeforePhaseReduction =
      inflatedMaxBenefitAmount -
      cumulativePolicyPayOut -
      (ltcPolicy.policyBenefitUtilizedToDate ?? 0);
  }

  let policySharedBenefitPoolRemaining;

  if (policyTypeHasSharedPool) {
    if (
      policyDataAccumulator.benefitPoolsExhausted.sharedPoolAmount &&
      policyDataAccumulator.benefitPoolsExhausted.clientIndividualPoolAmount &&
      policyDataAccumulator.benefitPoolsExhausted.partnerIndividualPoolAmount
    ) {
      policySharedBenefitPoolRemaining = 0;
      remainingMaxBenefitBeforePhaseReduction = 0;
      inflatedMaxBenefitAmount = 0;
    } else {
      const individualPoolAmount =
        ((policyMaximumBenefitAmount ?? 0) - (policyBenefitUtilizedToDate ?? 0)) / 3;

      ltcPolicy.policySharedBenefitPool = individualPoolAmount;

      const inflatedIndividualPoolAmount = calcPolicyInflatedMaxBenefitToEndOfPhase(
        mutableClient,
        phaseCosts.carePhase,
        policyPremiumStartYear,
        individualPoolAmount,
        policyInflationProtection ?? 0,
      );

      // total ltc benefit amount is 2/3 of the fully inflated max benefit amount
      // this accounts for the individual benefit pool and the shared benefit pool
      inflatedMaxBenefitAmount = inflatedIndividualPoolAmount;

      policySharedBenefitPoolRemaining = policyDataAccumulator.benefitPoolsExhausted
        .sharedPoolAmount
        ? 0
        : Math.max(
            inflatedIndividualPoolAmount - (policyDataAccumulator.sharedPoolUtilized ?? 0),
            0,
          );

      // calculate remaining shared pool
      remainingMaxBenefitBeforePhaseReduction = calcRemainingBenefitForShareCareRiderPlans(
        inflatedIndividualPoolAmount,
        policyDataAccumulator,
        isClientSessionClient,
        policySharedBenefitPoolRemaining,
      );
    }
  }

  let costCoverageAddedToTotal;

  const {
    portionOfPhaseCostBenefitEligible,
    maxPayoutAccordingToDailyMaximum,
    phaseMonthlyCost,
    phaseCostPerDayNeeded,
    phaseCareDaysNeeded,
    daysWhereLessThanTwoADLs,
    applicableWaitingPeriodDays,
    phaseEligibleCareDaysAfter2AdlRequirement,
    phaseEligibleCareDaysAfterWaitingPeriod,
    dailyBenefitAmount,
    inflationProtection,
    inflationPeriodYears,
    inflatedDailyBenefitAmount,
    inflatedDailyBenefitAmountBase,
    inflatedDailyBenefitAmountRider,
    inflatedFaceAmount,
    dailyBenefitAmountBase,
    baseBenefitAmountRemaining,
    baseAmountApplicableDays,
    riderAmountApplicableDays,
  } = calcSinglePhasePolicyCoverage(
    mutableClient,
    phaseCosts.appliedCareEnvironment,
    phaseCosts,
    policyDataAccumulator,
  );

  // coverage for phase cost is capped by whichever is less
  // 1) the portion of the phase cost that is benefit eligible
  // 2) the remaining max benefit
  // 3) the max payout according to daily maximum, if applicable
  const phaseCostCoverage = Math.min(
    portionOfPhaseCostBenefitEligible,
    remainingMaxBenefitBeforePhaseReduction,
    maxPayoutAccordingToDailyMaximum ?? Infinity,
  );
  let remainingMaxBenefitAfterPhaseReduction;

  // handle updates for reimbursement policy
  const isReimbursementPolicyPayment = !isIndemnityPolicyPayment;
  if (isReimbursementPolicyPayment) {
    // handle reimbursement policy
    costCoverageAddedToTotal = phaseCostCoverage;

    updateProjectedCostCoverage(policyDataAccumulator, isClientSessionClient, phaseCostCoverage);
    // update remaining max benefit
    policyDataAccumulator.cumulativePolicyPayOut += phaseCostCoverage;
    remainingMaxBenefitAfterPhaseReduction = Math.max(
      remainingMaxBenefitBeforePhaseReduction - phaseCostCoverage,
      0,
    );
  }

  let surplusAppliedToCoverage = 0;
  let indemnitySurplusForPhase = 0;
  let indemnityPayout;
  let sharedPoolUtilized;

  // handle updates for indemnity policy
  if (isIndemnityPolicyPayment) {
    // calculate indemnity payout
    indemnityPayout = maxPayoutAccordingToDailyMaximum
      ? Math.min(maxPayoutAccordingToDailyMaximum, remainingMaxBenefitBeforePhaseReduction)
      : phaseCostCoverage; // there should always be daily max, but just in case

    const indemnityResult = applyIndemnityPayoutToPhase({
      accumulator: policyDataAccumulator,
      isClientSessionClient,
      indemnityPayout,
      phaseCostCoverage,
      portionOfPhaseCostBenefitEligible,
      phaseInflatedProfessionalShareCost: phaseCosts.phaseInflatedProfessionalShareCost,
      remainingMaxBenefitBeforePhaseReduction,
    });
    surplusAppliedToCoverage = indemnityResult.surplusAppliedToCoverage;
    indemnitySurplusForPhase = indemnityResult.indemnitySurplusForPhase;
    costCoverageAddedToTotal = indemnityResult.costCoverageAddedToTotal;
    remainingMaxBenefitAfterPhaseReduction = indemnityResult.remainingMaxBenefitAfterPhaseReduction;
  }

  // Reimbursement policies can't pay more than actual incurred cost, so
  // cap the per-day reimbursement at the satisfaction phase's cost rate.
  // Indemnity policies pay the full daily benefit as a cash settlement —
  // no cost cap.
  const perDayEpReimbursement = isIndemnityPolicyPayment
    ? (inflatedDailyBenefitAmount ?? 0)
    : Math.min(inflatedDailyBenefitAmount ?? 0, phaseCostPerDayNeeded ?? 0);
  const epResult = applyRetroactiveEpReimbursement({
    ltcPolicy,
    accumulator: policyDataAccumulator,
    isClientSessionClient,
    applicableWaitingPeriodDays: applicableWaitingPeriodDays ?? 0,
    phaseHomeCareWaitingPeriodDaysRemaining,
    phaseFacilityCareWaitingPeriodDaysRemaining,
    perDayReimbursement: perDayEpReimbursement,
    remainingMaxBenefitAfterPhaseReduction: remainingMaxBenefitAfterPhaseReduction ?? 0,
  });
  const { epReimbursementAmount, epDaysReimbursed } = epResult;
  remainingMaxBenefitAfterPhaseReduction = epResult.remainingMaxBenefitAfterPhaseReduction;
  costCoverageAddedToTotal = (costCoverageAddedToTotal ?? 0) + epReimbursementAmount;

  if (policyTypeHasSharedPool) {
    sharedPoolUtilized = updateBenefitPoolExhaustedStatus(
      policyDataAccumulator as JointPolicyDataAccumulator,
      isClientSessionClient,
      inflatedMaxBenefitAmount,
      costCoverageAddedToTotal,
    );
  }

  // update phase calculations for whitebox explanations
  const phaseCalculations = {
    phase: phaseCosts.carePhase,
    phaseMonthlyCost: phaseMonthlyCost ?? 0,
    phaseCostPerDayNeeded: phaseCostPerDayNeeded ?? 0,
    phaseCareDaysNeeded: phaseCareDaysNeeded ?? 0,
    daysWhereLessThanTwoADLs: daysWhereLessThanTwoADLs ?? 0,
    applicableWaitingPeriodDays: applicableWaitingPeriodDays ?? 0,
    phaseEligibleCareDaysAfter2AdlRequirement: phaseEligibleCareDaysAfter2AdlRequirement ?? 0,
    phaseEligibleCareDaysAfterWaitingPeriod: phaseEligibleCareDaysAfterWaitingPeriod ?? 0,
    phaseFacilityCareWaitingPeriodDaysRemaining: phaseFacilityCareWaitingPeriodDaysRemaining ?? 0,
    phaseHomeCareWaitingPeriodDaysRemaining: phaseHomeCareWaitingPeriodDaysRemaining ?? 0,
    dailyBenefitAmount: dailyBenefitAmount ?? 0,
    inflationProtection: inflationProtection ?? 0,
    inflationPeriodYears: inflationPeriodYears ?? 0,
    inflatedDailyBenefitAmount: inflatedDailyBenefitAmount ?? 0,
    inflatedMaxBenefitAmount: inflatedMaxBenefitAmount ?? 0,
    maxPayoutAccordingToDailyMaximum: maxPayoutAccordingToDailyMaximum ?? 0,
    surplusAppliedToCoverage: surplusAppliedToCoverage ?? 0,
    indemnitySurplusForPhase: indemnitySurplusForPhase ?? 0,
    phaseCostCoverage: phaseCostCoverage ?? 0,
    costCoverageAddedToTotal: costCoverageAddedToTotal ?? 0,
    portionOfPhaseCostBenefitEligible: portionOfPhaseCostBenefitEligible ?? 0,
    remainingMaxBenefitBeforePhaseReduction: remainingMaxBenefitBeforePhaseReduction ?? 0,
    remainingMaxBenefitAfterPhaseReduction: remainingMaxBenefitAfterPhaseReduction ?? 0,
    cumulativePolicyPayOut: cumulativePolicyPayOut ?? 0,
    indemnityPayout: indemnityPayout ?? 0,
    dailyBenefitAmountBase: dailyBenefitAmountBase ?? 0,
    baseBenefitAmountRemaining: baseBenefitAmountRemaining ?? 0,
    inflatedFaceAmount: inflatedFaceAmount ?? 0,
    baseAmountApplicableDays: baseAmountApplicableDays ?? 0,
    riderAmountApplicableDays: riderAmountApplicableDays ?? 0,
    indemnitySurplusBeforePhaseReduction: indemnitySurplus ?? 0,
    policySharedBenefitPoolRemaining: policySharedBenefitPoolRemaining ?? null,
    inflatedDailyBenefitAmountBase: inflatedDailyBenefitAmountBase ?? 0,
    inflatedDailyBenefitAmountRider: inflatedDailyBenefitAmountRider ?? 0,
    sharedPoolUtilized: sharedPoolUtilized ?? 0,
    epReimbursementAmount,
    epDaysReimbursed,
  };

  updatePhaseCalculations(
    policyDataAccumulator,
    isClientSessionClient,
    phaseIndex,
    phaseCalculations,
  );
}

// Resolve CareMatters daily/monthly base benefit. Plan §"Per-phase
// orchestration": daily fields on CM II illustrations are equal across all
// four care environments. Take the max defensively in case legacy data has
// only one of the four populated.
function getMaxMonthlyBenefitAmount(ltcPolicy: PolicyFundingSource): number {
  const home = ltcPolicy.dailyHomeBenefitAmount ?? 0;
  const al = ltcPolicy.dailyAssistedLivingBenefitAmount ?? 0;
  const nh = ltcPolicy.dailyNursingHomeBenefitAmount ?? 0;
  const il = ltcPolicy.dailyIndependentLivingBenefitAmount ?? 0;
  const dailyMax = Math.max(home, al, nh, il);
  return dailyMax * 30;
}

// Backward compat: CM II policies added before policyBenefitPeriodMonths was
// mapped from the quote may have a null value on the policy. Fall back to the
// source quote's ltcBenefitDurationMonths (PolicyQuote only — TraditionalLTCQuote
// doesn't carry it) so existing plans still calculate. sourceQuote can be a
// parsed object or a JSON string at runtime, so normalize before reading.
//
// Returns 0 when both fields are missing — gates `shouldUseCareMattersCalc`
// so policies without the data fall back to the legacy hybrid path instead
// of silently producing zero LTC benefit.
export function getCareMattersBenefitPeriodMonths(ltcPolicy: PolicyFundingSource): number {
  if (!isNullOrUndefined(ltcPolicy.policyBenefitPeriodMonths)) {
    return ltcPolicy.policyBenefitPeriodMonths;
  }
  const rawQuote: unknown = ltcPolicy.sourceQuote;
  if (!rawQuote) return 0;
  let quote: unknown = rawQuote;
  if (typeof quote === 'string') {
    try {
      quote = JSON.parse(quote);
    } catch {
      return 0;
    }
  }
  if (quote && typeof quote === 'object' && 'ltcBenefitDurationMonths' in quote) {
    const value = (quote as { ltcBenefitDurationMonths?: unknown }).ltcBenefitDurationMonths;
    return typeof value === 'number' ? value : 0;
  }
  return 0;
}

// Backward compat gate. Pre-PR, CM family policies were calculated through
// the hybridLifeInsurance calculationType path (FundingPolicyTypeDef.ts
// still maps both careMattersTwo and jointCareMatters to that
// calculationType). The dedicated CM path needs a benefit period to size
// the base pool — without it, base-pool dollars are 0 and the policy would
// silently project no LTC coverage. Routing legacy CM rows (no
// policyBenefitPeriodMonths, no parseable sourceQuote.ltcBenefitDurationMonths)
// back through the hybrid path preserves their pre-rollout numbers.
//
// Both the calc (calcFundingSourceDerivatives) and the advisor whitebox
// (InsuranceWhiteBoxSlideOutContent) route through this — they must agree
// or the displayed explanation won't match the numbers.
export function shouldUseCareMattersCalc(ltcPolicy: PolicyFundingSource): boolean {
  if (!isCareMattersFamily(ltcPolicy.policyType)) return false;
  return getCareMattersBenefitPeriodMonths(ltcPolicy) > 0;
}

function getCareMattersRiderForm(ltcPolicy: PolicyFundingSource): CareMattersRiderForm {
  // policyInflationProtection is stored as a decimal (e.g. 0.03 for 3%) —
  // matches the rest of the inflation calc (calcFutureValue) and the
  // extraction-side normalization in usePolicyForm (divides by 100).
  const capYearsRaw = ltcPolicy.policyInflationProtectionYears;
  return {
    rate: ltcPolicy.policyInflationProtection ?? 0,
    simple: ltcPolicy.isSimpleInflationProtection === true,
    capYears: capYearsRaw && capYearsRaw > 0 ? capYearsRaw : null,
  };
}

function processSinglePhaseCareMatters<AccumlatorType extends PolicyDataAccumulatorBase>(
  sessionClientId: string,
  mutableClient: Draft<Client>,
  policyDataAccumulator: AccumlatorType,
  phaseCosts: SinglePhaseCosts,
): void {
  const {
    fundingSources: {
      ltcPolicy,
      ltcPolicy: { policyPremiumStartYear, policyMaximumBenefitAmount },
    },
    clientPhasePredictedEndYears,
    clientPhasePredictedStartYears,
  } = mutableClient;

  if (isNullOrUndefined(policyPremiumStartYear)) {
    throw new Error('Policy premium start year is not defined');
  }

  const {
    isClientSessionClient,
    phaseIndex,
    phaseHomeCareWaitingPeriodDaysRemaining,
    phaseFacilityCareWaitingPeriodDaysRemaining,
    indemnitySurplusBeforePhase,
    cumulativePolicyPayOutBefore,
  } = derivePhaseContext(sessionClientId, mutableClient, policyDataAccumulator, phaseCosts);

  // 1. Eligibility — reuse existing pipeline (handles 2-ADL, retroactive WP).
  const {
    phaseEligibleCareDays: phaseBenefitEligibleCareDays,
    daysWhereLessThanTwoADLs,
    applicableWaitingPeriodDays,
    phaseEligibleCareDaysAfter2AdlRequirement,
    phaseEligibleCareDaysAfterWaitingPeriod,
  } = handlePhaseBenefitEligibleCareDays(mutableClient, phaseCosts);

  const {
    phaseCareDaysNeeded,
    phaseMonthlyCost,
    phaseCostPerDayNeeded,
    portionOfPhaseCostBenefitEligible,
  } = derivePhaseCostMetrics(phaseCosts, phaseBenefitEligibleCareDays);

  // 2. Day → month conversion. Truncated tail (≤ 14 days at phase boundary)
  // is uncovered — matches CM II's rider forfeiture rule (partial months
  // pay no rider).
  const phaseEligibleMonths = Math.floor(phaseEligibleCareDaysAfterWaitingPeriod / DAYS_IN_MONTH);

  // 3. Resolve base monthly + rider form.
  const baseMonthly = getMaxMonthlyBenefitAmount(ltcPolicy);
  const dailyBenefitAmount = baseMonthly / DAYS_IN_MONTH;
  const riderForm = getCareMattersRiderForm(ltcPolicy);

  // 4. Resolve base pool remaining. Total pool = baseMonthly ×
  // benefitPeriodMonths, less consumption carried on the accumulator.
  // The accumulator is seeded with policyBenefitUtilizedToDate at calc
  // start (see calcPolicyCoverageImpl) and grows phase by phase.
  const benefitPeriodMonths = getCareMattersBenefitPeriodMonths(ltcPolicy);
  const totalBasePool = baseMonthly * benefitPeriodMonths;
  const basePoolRemainingBeforePhase = Math.max(
    totalBasePool - policyDataAccumulator.baseDollarsPaid,
    0,
  );

  // Phase policy year — base it on the phase end year so rider growth
  // applies across the phase (consistent with existing inflation logic
  // that uses clientPhasePredictedEndYears).
  const phaseEndYear = clientPhasePredictedEndYears[phaseCosts.carePhase] ?? policyPremiumStartYear;
  // Use the phase START year for rider lookup so within-phase year-by-year
  // walking in simulateCareMattersPhase is correct. Prefer the
  // precomputed predicted start year; fall back to deriving from the end
  // year only if the predicted start is missing.
  const phaseStartYear =
    clientPhasePredictedStartYears[phaseCosts.carePhase] ??
    Math.max(
      phaseEndYear - Math.ceil(phaseCosts.phaseCareMonthsNeeded / 12),
      policyPremiumStartYear,
    );
  const phasePolicyStartYear = Math.max(1, phaseStartYear - policyPremiumStartYear + 1);

  // 5. Run the phase simulation.
  const sim = simulateCareMattersPhase({
    baseMonthly,
    basePoolRemaining: basePoolRemainingBeforePhase,
    riderForm,
    phasePolicyStartYear,
    phaseEligibleMonths,
  });

  // 6. Apply indemnity surplus accounting. Phase cost coverage is the
  // dollars actually applied to the cost — capped by indemnity payout
  // and benefit-eligible portion of the cost.
  const indemnityPayout = sim.indemnityPayoutForPhase;
  const phaseCostCoverage = Math.min(portionOfPhaseCostBenefitEligible, indemnityPayout);

  let costCoverageAddedToTotal = 0;
  let surplusAppliedToCoverage = 0;
  let indemnitySurplusForPhase = 0;
  let remainingMaxBenefitAfterPhaseReduction = basePoolRemainingBeforePhase;

  if (indemnityPayout > 0 || indemnitySurplusBeforePhase > 0) {
    const indemnityResult = applyIndemnityPayoutToPhase({
      accumulator: policyDataAccumulator,
      isClientSessionClient,
      indemnityPayout,
      phaseCostCoverage,
      portionOfPhaseCostBenefitEligible,
      phaseInflatedProfessionalShareCost: phaseCosts.phaseInflatedProfessionalShareCost,
      remainingMaxBenefitBeforePhaseReduction: basePoolRemainingBeforePhase,
      // CM II: only base-dollar payments deplete the pool; rider dollars
      // are a separate stream.
      poolDepletingDollars: sim.baseDollarsPaidThisPhase,
    });
    costCoverageAddedToTotal = indemnityResult.costCoverageAddedToTotal;
    surplusAppliedToCoverage = indemnityResult.surplusAppliedToCoverage;
    indemnitySurplusForPhase = indemnityResult.indemnitySurplusForPhase;
    remainingMaxBenefitAfterPhaseReduction = indemnityResult.remainingMaxBenefitAfterPhaseReduction;
  }

  // 7. Update CM II accumulator state.
  policyDataAccumulator.baseDollarsPaid += sim.baseDollarsPaidThisPhase;
  policyDataAccumulator.riderDollarsPaid += sim.riderDollarsPaidThisPhase;

  // 8. Retroactive elimination-period reimbursement. CM II is indemnity —
  // pays the full base daily as a cash settlement, no cost cap. The
  // settlement also consumes base-pool dollars (it's a payout of daily
  // base benefits already served during the WP).
  const epResult = applyRetroactiveEpReimbursement({
    ltcPolicy,
    accumulator: policyDataAccumulator,
    isClientSessionClient,
    applicableWaitingPeriodDays: applicableWaitingPeriodDays ?? 0,
    phaseHomeCareWaitingPeriodDaysRemaining,
    phaseFacilityCareWaitingPeriodDaysRemaining,
    perDayReimbursement: dailyBenefitAmount,
    remainingMaxBenefitAfterPhaseReduction,
    onConsumed: amount => {
      policyDataAccumulator.baseDollarsPaid += amount;
    },
  });
  const { epReimbursementAmount, epDaysReimbursed } = epResult;
  remainingMaxBenefitAfterPhaseReduction = epResult.remainingMaxBenefitAfterPhaseReduction;
  costCoverageAddedToTotal += epReimbursementAmount;

  // 9. Stash policy-level totals on first phase. Total LTC Benefit is set
  // in calcPolicySummaryStats for CM II — leave policyTotalLTCBenefit
  // unset here (the summary stats branch computes it from
  // calcTotalLtcBenefitOverPeriod).

  // 10. Populate SinglePhaseCalculations.
  const averageRiderMonthly = sim.averageRiderMonthlyForPhase;
  const inflatedDailyBenefitAmountRider = averageRiderMonthly / DAYS_IN_MONTH;
  const inflatedDailyBenefitAmount = dailyBenefitAmount + inflatedDailyBenefitAmountRider;
  const inflationPeriodYears = Math.min(
    Math.max(phaseEndYear - policyPremiumStartYear, 0),
    ltcPolicy.policyInflationProtectionYears ?? Infinity,
  );

  const phaseCalculations: SinglePhaseCalculations = {
    phase: phaseCosts.carePhase,
    phaseMonthlyCost,
    phaseCostPerDayNeeded,
    phaseCareDaysNeeded,
    daysWhereLessThanTwoADLs: daysWhereLessThanTwoADLs ?? 0,
    applicableWaitingPeriodDays: applicableWaitingPeriodDays ?? 0,
    phaseEligibleCareDaysAfter2AdlRequirement: phaseEligibleCareDaysAfter2AdlRequirement ?? 0,
    phaseEligibleCareDaysAfterWaitingPeriod: phaseEligibleCareDaysAfterWaitingPeriod ?? 0,
    phaseHomeCareWaitingPeriodDaysRemaining,
    phaseFacilityCareWaitingPeriodDaysRemaining,
    dailyBenefitAmount,
    inflationProtection: ltcPolicy.policyInflationProtection ?? 0,
    inflationPeriodYears,
    inflatedDailyBenefitAmount,
    // CM II base is flat — base daily never grows.
    inflatedDailyBenefitAmountBase: dailyBenefitAmount,
    inflatedDailyBenefitAmountRider,
    // Face is fixed for CM II — does not inflate.
    inflatedFaceAmount: policyMaximumBenefitAmount ?? 0,
    inflatedMaxBenefitAmount: indemnityPayout,
    maxPayoutAccordingToDailyMaximum: phaseEligibleMonths * (baseMonthly + averageRiderMonthly),
    dailyBenefitAmountBase: dailyBenefitAmount,
    baseBenefitAmountRemaining: sim.basePoolRemainingAfterPhase,
    baseAmountApplicableDays: sim.monthsCovered * DAYS_IN_MONTH,
    riderAmountApplicableDays: sim.monthsCovered * DAYS_IN_MONTH,
    phaseCostCoverage,
    costCoverageAddedToTotal,
    portionOfPhaseCostBenefitEligible,
    surplusAppliedToCoverage,
    indemnitySurplusForPhase,
    indemnitySurplusBeforePhaseReduction: indemnitySurplusBeforePhase,
    remainingMaxBenefitBeforePhaseReduction: basePoolRemainingBeforePhase,
    remainingMaxBenefitAfterPhaseReduction,
    cumulativePolicyPayOut: cumulativePolicyPayOutBefore,
    indemnityPayout,
    // Joint CM Together: the shared pool IS the base pool. Individual
    // policies have no shared pool concept. Rider dollars are a
    // separate stream and don't deplete the shared pool.
    policySharedBenefitPoolRemaining: isJointPolicyDataAccumulator(policyDataAccumulator)
      ? sim.basePoolRemainingAfterPhase
      : null,
    sharedPoolUtilized: isJointPolicyDataAccumulator(policyDataAccumulator)
      ? policyDataAccumulator.baseDollarsPaid
      : null,
    epReimbursementAmount,
    epDaysReimbursed,
  };

  updatePhaseCalculations(
    policyDataAccumulator,
    isClientSessionClient,
    phaseIndex,
    phaseCalculations,
  );
}

function updateBenefitPoolExhaustedStatus(
  policyDataAccumulator: JointPolicyDataAccumulator,
  isClientSessionClient: boolean,
  inflatedMaxBenefitAmount: number,
  costCoverageAddedToTotal: number | undefined,
): number | undefined {
  const { projectedCostCoverage, partnerProjectedCostCoverage } = policyDataAccumulator;

  let sharedPoolUtilized: number | undefined;

  if (isClientSessionClient) {
    sharedPoolUtilized = updateClientPoolStatus(
      policyDataAccumulator,
      projectedCostCoverage,
      inflatedMaxBenefitAmount,
      costCoverageAddedToTotal,
    );
  } else {
    sharedPoolUtilized = updatePartnerPoolStatus(
      policyDataAccumulator,
      partnerProjectedCostCoverage,
      inflatedMaxBenefitAmount,
      costCoverageAddedToTotal,
    );
  }

  updateSharedPoolStatus(policyDataAccumulator, inflatedMaxBenefitAmount);

  return sharedPoolUtilized;
}

function updateClientPoolStatus(
  accumulator: JointPolicyDataAccumulator,
  projectedCoverage: number,
  maxBenefit: number,
  costCoverageAdded: number | undefined,
): number | undefined {
  let sharedPoolUtilized: number | undefined;

  // Handle already exhausted pool
  if (accumulator.benefitPoolsExhausted.clientIndividualPoolAmount) {
    sharedPoolUtilized = accumulator.sharedPoolUtilized + (costCoverageAdded ?? 0);
    accumulator.sharedPoolUtilized = sharedPoolUtilized;
    return sharedPoolUtilized;
  }

  // Check if pool becomes exhausted
  if (projectedCoverage >= maxBenefit) {
    accumulator.benefitPoolsExhausted.clientIndividualPoolAmount = true;
    sharedPoolUtilized =
      accumulator.sharedPoolUtilized + Math.max(0, projectedCoverage - maxBenefit);
    accumulator.sharedPoolUtilized = sharedPoolUtilized;
  }

  return sharedPoolUtilized;
}

function updatePartnerPoolStatus(
  accumulator: JointPolicyDataAccumulator,
  projectedCoverage: number,
  maxBenefit: number,
  costCoverageAdded: number | undefined,
): number | undefined {
  let sharedPoolUtilized: number | undefined;

  // Handle already exhausted pool
  if (accumulator.benefitPoolsExhausted.partnerIndividualPoolAmount) {
    sharedPoolUtilized = accumulator.sharedPoolUtilized + (costCoverageAdded ?? 0);
    accumulator.sharedPoolUtilized = sharedPoolUtilized;
    return sharedPoolUtilized;
  }

  // Check if pool becomes exhausted
  if (projectedCoverage >= maxBenefit) {
    accumulator.benefitPoolsExhausted.partnerIndividualPoolAmount = true;
    sharedPoolUtilized =
      accumulator.sharedPoolUtilized + Math.max(0, projectedCoverage - maxBenefit);
    accumulator.sharedPoolUtilized = sharedPoolUtilized;
  }

  return sharedPoolUtilized;
}

function updateSharedPoolStatus(accumulator: JointPolicyDataAccumulator, maxBenefit: number) {
  if (
    !accumulator.benefitPoolsExhausted.sharedPoolAmount &&
    accumulator.sharedPoolUtilized &&
    accumulator.sharedPoolUtilized >= maxBenefit
  ) {
    accumulator.benefitPoolsExhausted.sharedPoolAmount = true;
  }
}

function updateProjectedCostCoverage(
  policyDataAccumulator: PolicyDataAccumulatorBase,
  isClientSessionClient: boolean,
  coverageToAdd: number,
): void {
  if (isClientSessionClient) {
    policyDataAccumulator.projectedCostCoverage += coverageToAdd;
  } else if (isJointPolicyDataAccumulator(policyDataAccumulator)) {
    policyDataAccumulator.partnerProjectedCostCoverage += coverageToAdd;
  } else {
    throw new Error('Accumulator is not a JointPolicyDataAccumulator when expected');
  }
}

// Indemnity-policy phase accounting. Mutates accumulator surplus,
// projectedCostCoverage, and cumulativePolicyPayOut. Returns the per-phase
// values needed for phase-calculation output.
//
// Two cases:
//   1. Surplus exists AND phase coverage falls short of benefit-eligible
//      cost → drain surplus into coverage, no new surplus generated.
//   2. Otherwise → coverage = phaseCostCoverage; any indemnity payout above
//      the phase cost becomes new surplus carried to the next phase.
//
// Shared by the legacy hybrid path and the CareMatters II path. By
// default the pool clamp uses `indemnityPayout` (legacy behavior — the
// full payout depletes the pool). For products like CM II where only
// part of the payout depletes the pool (rider dollars don't), pass
// `poolDepletingDollars` explicitly.
export function applyIndemnityPayoutToPhase({
  accumulator,
  isClientSessionClient,
  indemnityPayout,
  phaseCostCoverage,
  portionOfPhaseCostBenefitEligible,
  phaseInflatedProfessionalShareCost,
  remainingMaxBenefitBeforePhaseReduction,
  poolDepletingDollars,
}: {
  accumulator: PolicyDataAccumulatorBase;
  isClientSessionClient: boolean;
  indemnityPayout: number;
  phaseCostCoverage: number;
  portionOfPhaseCostBenefitEligible: number;
  phaseInflatedProfessionalShareCost: number;
  remainingMaxBenefitBeforePhaseReduction: number;
  poolDepletingDollars?: number;
}): {
  surplusAppliedToCoverage: number;
  indemnitySurplusForPhase: number;
  costCoverageAddedToTotal: number;
  remainingMaxBenefitAfterPhaseReduction: number;
} {
  const indemnitySurplus = accumulator.indemnitySurplus;
  let surplusAppliedToCoverage = 0;
  let indemnitySurplusForPhase = 0;
  let costCoverageAddedToTotal: number;

  if (indemnitySurplus > 0 && phaseCostCoverage < portionOfPhaseCostBenefitEligible) {
    const remainingCostToCover = phaseInflatedProfessionalShareCost - phaseCostCoverage;
    surplusAppliedToCoverage = Math.min(indemnitySurplus, remainingCostToCover);
    costCoverageAddedToTotal = phaseCostCoverage + surplusAppliedToCoverage;
    updateProjectedCostCoverage(accumulator, isClientSessionClient, costCoverageAddedToTotal);
    accumulator.indemnitySurplus -= surplusAppliedToCoverage;
  } else {
    costCoverageAddedToTotal = phaseCostCoverage;
    updateProjectedCostCoverage(accumulator, isClientSessionClient, costCoverageAddedToTotal);
    indemnitySurplusForPhase = Math.max(indemnityPayout - phaseCostCoverage, 0);
    accumulator.indemnitySurplus += indemnitySurplusForPhase;
  }

  accumulator.cumulativePolicyPayOut += indemnityPayout;
  const poolDepletion = poolDepletingDollars ?? indemnityPayout;
  const remainingMaxBenefitAfterPhaseReduction = Math.max(
    remainingMaxBenefitBeforePhaseReduction - poolDepletion,
    0,
  );

  return {
    surplusAppliedToCoverage,
    indemnitySurplusForPhase,
    costCoverageAddedToTotal,
    remainingMaxBenefitAfterPhaseReduction,
  };
}

// Phase cost arithmetic. Pure derivation from `phaseCosts` plus the
// eligible-day count returned by `handlePhaseBenefitEligibleCareDays`.
// Guards against zero `phaseCareMonthsNeeded` so callers don't divide by
// zero on phases the client skips.
const DAYS_IN_MONTH = 30;
function derivePhaseCostMetrics(
  phaseCosts: SinglePhaseCosts,
  phaseBenefitEligibleCareDays: number,
): {
  phaseCareDaysNeeded: number;
  phaseMonthlyCost: number;
  phaseCostPerDayNeeded: number;
  portionOfPhaseCostBenefitEligible: number;
} {
  const { phaseCareMonthsNeeded, phaseInflatedProfessionalShareCost } = phaseCosts;
  const phaseCareDaysNeeded = phaseCareMonthsNeeded * DAYS_IN_MONTH;
  const phaseMonthlyCost =
    phaseCareMonthsNeeded > 0 ? phaseInflatedProfessionalShareCost / phaseCareMonthsNeeded : 0;
  const phaseCostPerDayNeeded =
    phaseCareDaysNeeded > 0 ? phaseInflatedProfessionalShareCost / phaseCareDaysNeeded : 0;
  const portionOfPhaseCostBenefitEligible = phaseBenefitEligibleCareDays * phaseCostPerDayNeeded;
  return {
    phaseCareDaysNeeded,
    phaseMonthlyCost,
    phaseCostPerDayNeeded,
    portionOfPhaseCostBenefitEligible,
  };
}

// Boilerplate at the top of every per-phase function: phase index, who
// the session client is, the waiting-period day counts before this phase
// runs (eligibility processing decrements them in place), and the
// "before" values of accumulator state needed for the phase output.
function derivePhaseContext(
  sessionClientId: string,
  mutableClient: Draft<Client>,
  policyDataAccumulator: PolicyDataAccumulatorBase,
  phaseCosts: SinglePhaseCosts,
): {
  isClientSessionClient: boolean;
  phaseIndex: number;
  phaseHomeCareWaitingPeriodDaysRemaining: number;
  phaseFacilityCareWaitingPeriodDaysRemaining: number;
  indemnitySurplusBeforePhase: number;
  cumulativePolicyPayOutBefore: number;
} {
  const { ltcPolicy } = mutableClient.fundingSources;
  return {
    isClientSessionClient: sessionClientId === mutableClient.clientId,
    phaseIndex: carePhaseDefs[phaseCosts.carePhase].index,
    phaseHomeCareWaitingPeriodDaysRemaining: ltcPolicy.homeCareWaitingPeriodDaysRemaining ?? 0,
    phaseFacilityCareWaitingPeriodDaysRemaining:
      ltcPolicy.facilityCareWaitingPeriodDaysRemaining ?? 0,
    indemnitySurplusBeforePhase: policyDataAccumulator.indemnitySurplus,
    cumulativePolicyPayOutBefore: policyDataAccumulator.cumulativePolicyPayOut,
  };
}

// Retroactive elimination-period reimbursement: once EP is satisfied
// during the claim, the served days are paid as a lump sum at the
// per-day reimbursement rate. Bypasses daily-max (it's a settlement, not
// a daily payout) but is still clamped by the remaining benefit pool.
// For joint policies each spouse tracks their own EP state on their
// cloned ltcPolicy, so the lump fires once per spouse.
//
// The per-day rate is the only thing that varies by product:
//   - Indemnity hybrid: full inflated daily benefit
//   - Reimbursement hybrid: min(inflated daily, phase cost per day)
//   - CareMatters II: flat base daily benefit
//
// CM II additionally consumes base-pool dollars; pass `onConsumed` to
// have the helper bump `accumulator.baseDollarsPaid`.
function applyRetroactiveEpReimbursement({
  ltcPolicy,
  accumulator,
  isClientSessionClient,
  applicableWaitingPeriodDays,
  phaseHomeCareWaitingPeriodDaysRemaining,
  phaseFacilityCareWaitingPeriodDaysRemaining,
  perDayReimbursement,
  remainingMaxBenefitAfterPhaseReduction,
  onConsumed,
}: {
  ltcPolicy: Draft<PolicyFundingSource>;
  accumulator: PolicyDataAccumulatorBase;
  isClientSessionClient: boolean;
  applicableWaitingPeriodDays: number;
  phaseHomeCareWaitingPeriodDaysRemaining: number;
  phaseFacilityCareWaitingPeriodDaysRemaining: number;
  perDayReimbursement: number;
  remainingMaxBenefitAfterPhaseReduction: number;
  onConsumed?: (amount: number) => void;
}): {
  epReimbursementAmount: number;
  epDaysReimbursed: number;
  remainingMaxBenefitAfterPhaseReduction: number;
} {
  if (!ltcPolicy.hasRetroactiveWaitingPeriodReimbursement || ltcPolicy.epReimbursed) {
    return {
      epReimbursementAmount: 0,
      epDaysReimbursed: 0,
      remainingMaxBenefitAfterPhaseReduction,
    };
  }

  ltcPolicy.epDaysServed = (ltcPolicy.epDaysServed ?? 0) + (applicableWaitingPeriodDays ?? 0);

  const homeJustSatisfied =
    phaseHomeCareWaitingPeriodDaysRemaining > 0 &&
    (ltcPolicy.homeCareWaitingPeriodDaysRemaining ?? 0) === 0;
  const facilityJustSatisfied =
    phaseFacilityCareWaitingPeriodDaysRemaining > 0 &&
    (ltcPolicy.facilityCareWaitingPeriodDaysRemaining ?? 0) === 0;

  if (!homeJustSatisfied && !facilityJustSatisfied) {
    return {
      epReimbursementAmount: 0,
      epDaysReimbursed: 0,
      remainingMaxBenefitAfterPhaseReduction,
    };
  }

  const daysServed = ltcPolicy.epDaysServed ?? 0;
  const uncappedReimbursement = daysServed * perDayReimbursement;
  const epReimbursementAmount = Math.min(
    uncappedReimbursement,
    Math.max(remainingMaxBenefitAfterPhaseReduction, 0),
  );
  ltcPolicy.epReimbursed = true;

  updateProjectedCostCoverage(accumulator, isClientSessionClient, epReimbursementAmount);
  accumulator.cumulativePolicyPayOut += epReimbursementAmount;
  onConsumed?.(epReimbursementAmount);

  return {
    epReimbursementAmount,
    epDaysReimbursed: daysServed,
    remainingMaxBenefitAfterPhaseReduction: Math.max(
      remainingMaxBenefitAfterPhaseReduction - epReimbursementAmount,
      0,
    ),
  };
}

function updatePhaseCalculations(
  policyDataAccumulator: PolicyDataAccumulatorBase,
  isClientSessionClient: boolean,
  phaseIndex: number,
  phaseCalculations: SinglePhaseCalculations,
): void {
  if (isClientSessionClient) {
    policyDataAccumulator.phaseCalculations[phaseIndex] = phaseCalculations;
  } else if (isJointPolicyDataAccumulator(policyDataAccumulator)) {
    policyDataAccumulator.partnerPhaseCalculations[phaseIndex] = phaseCalculations;
  } else {
    throw new Error('Accumulator is not a JointPolicyDataAccumulator when expected');
  }
}

function calcHybridPolicyTotalValue({
  ltcPolicy,
  totalProjectedCostCoverage,
  policyInflatedDeathBenefitAmount,
  indemnitySurplus,
}: {
  ltcPolicy: PolicyFundingSource;
  totalProjectedCostCoverage: number;
  policyInflatedDeathBenefitAmount: number;
  indemnitySurplus: number;
}): number {
  const {
    policyType,
    policyMaximumBenefitAmount,
    policyBenefitIsUnlimited,
    policyMinimumGuaranteedBenefitAmount,
  } = ltcPolicy;

  if (policyBenefitIsUnlimited) {
    const remainingDeathBenefitAmount =
      policyInflatedDeathBenefitAmount - totalProjectedCostCoverage;

    return (
      totalProjectedCostCoverage +
      Math.max(remainingDeathBenefitAmount, policyMinimumGuaranteedBenefitAmount ?? 0, 0) +
      indemnitySurplus
    );
  }

  // Coverage is greater than death benefit
  const deathBenefit = hasFlatDeathBenefit(policyType)
    ? (policyMaximumBenefitAmount ?? 0)
    : policyInflatedDeathBenefitAmount;

  if (totalProjectedCostCoverage >= deathBenefit) {
    return (
      totalProjectedCostCoverage + (policyMinimumGuaranteedBenefitAmount ?? 0) + indemnitySurplus
    );
  }

  // Coverage is less than death benefit amount
  const policyCoveragePlusIndemnitySurplus = totalProjectedCostCoverage + indemnitySurplus;

  return (
    totalProjectedCostCoverage +
    Math.max(
      policyMinimumGuaranteedBenefitAmount ?? 0,
      deathBenefit - policyCoveragePlusIndemnitySurplus,
      0,
    ) +
    indemnitySurplus
  );
}

export function calcPolicySummaryStats(
  mutableClient: Draft<Client>,
  policyDataAccumulator: PolicyDataAccumulator | JointPolicyDataAccumulator,
) {
  const {
    fundingSources: {
      ltcPolicy: {
        policyType,
        isIndemnityPolicyPayment,
        policyPremiumStartYear,
        policyMaximumBenefitAmount,
        policyInflationProtectionOnFaceAmount,
        policyInflationProtection,
        policyInflationProtectionYears,
      },
      ltcPolicy,
    },
  } = mutableClient;

  const { projectedCostCoverage, indemnitySurplus } = policyDataAccumulator;
  const totalProjectedCostCoverage = isJointPolicyDataAccumulator(policyDataAccumulator)
    ? projectedCostCoverage + policyDataAccumulator.partnerProjectedCostCoverage
    : projectedCostCoverage;

  // CareMatters II / Together — flat-base + per-policy-year-rider mechanics.
  // Death benefit reduced by base dollars paid only (rider payments don't
  // reduce DB), floored at the stored minimum guaranteed amount when set.
  // Joint variant: a single shared joint policy pays one combined DB on
  // second death; specifiedAmount is read from the policy as-is. Legacy CM
  // rows lacking benefit-period data fall through to the hybrid summary.
  if (shouldUseCareMattersCalc(ltcPolicy)) {
    const isJoint =
      isJointPolicyDataAccumulator(policyDataAccumulator) && !!mutableClient.mutableClientPartner;
    const partnerLtcPolicy = isJoint
      ? mutableClient.mutableClientPartner!.fundingSources?.ltcPolicy
      : undefined;

    // Claim start year drives the policy-year of the rider stream when
    // the rider projection is built. For joint, the policy starts paying
    // when the FIRST insured needs care, so use the earlier of the two
    // onset years. Defensively: a missing ltcAtYear falls back to the
    // policy purchase year (claim "starts" at issue → policy year 1).
    const fallbackClaimYear = policyPremiumStartYear ?? new Date().getFullYear();
    const resolveClaimStart = (
      hasStarted: boolean,
      activationYear: number | null | undefined,
      ltcAtYear: number | null | undefined,
    ) => {
      const candidate = hasStarted ? (activationYear ?? ltcAtYear) : ltcAtYear;
      return candidate && Number.isFinite(candidate) ? candidate : fallbackClaimYear;
    };
    const selfClaimStart = resolveClaimStart(
      mutableClient.intakeSurvey.clientHasStartedLtc,
      ltcPolicy.policyActivationYear,
      mutableClient.appliedInferenceSet.ltcAtYear,
    );
    const partnerClaimStart =
      isJoint && mutableClient.mutableClientPartner
        ? resolveClaimStart(
            mutableClient.mutableClientPartner.intakeSurvey.clientHasStartedLtc,
            partnerLtcPolicy?.policyActivationYear,
            mutableClient.mutableClientPartner.appliedInferenceSet.ltcAtYear,
          )
        : selfClaimStart;
    const claimStartCalendarYear = Math.min(selfClaimStart, partnerClaimStart);
    const claimStartPolicyYear = Math.max(
      1,
      claimStartCalendarYear - (policyPremiumStartYear ?? claimStartCalendarYear) + 1,
    );

    const baseMonthly = getMaxMonthlyBenefitAmount(ltcPolicy);
    const benefitPeriodMonths = getCareMattersBenefitPeriodMonths(ltcPolicy);
    const riderForm = getCareMattersRiderForm(ltcPolicy);

    ltcPolicy.policyTotalLTCBenefit = calcTotalLtcBenefitOverPeriod({
      baseMonthly,
      benefitPeriodMonths,
      claimStartPolicyYear,
      riderForm,
    });

    // Joint CM Together: the policy is stored ONCE on the session
    // client and cloned to partner via copyJointPolicyToClientPartner.
    // Both sides therefore see the same face / floor; don't sum. The
    // single stored specifiedAmount IS the combined-DB-on-second-death
    // value the joint policy will pay. (Q9 lock.)
    const specifiedAmount = policyMaximumBenefitAmount ?? 0;
    const storedFloorAmount = ltcPolicy.policyMinimumGuaranteedBenefitAmount;

    const deathBenefit = calcCareMattersDeathBenefit({
      specifiedAmount,
      baseDollarsPaid: policyDataAccumulator.baseDollarsPaid,
      storedFloorAmount: storedFloorAmount ?? null,
    });

    ltcPolicy.policyInflatedDeathBenefitAmount = specifiedAmount;
    ltcPolicy.policyTotalValue = totalProjectedCostCoverage + deathBenefit + indemnitySurplus;
    ltcPolicy.policyNonLtcPayout = (ltcPolicy.policyTotalValue ?? 0) - totalProjectedCostCoverage;
    return;
  }

  // For traditional ltci, stci, annuity hybrids
  if (
    checkPolicyCalculationTypeEquals(policyType, [
      FundingPolicyType.longTermCareInsurance,
      FundingPolicyType.shortTermCareInsurance,
      FundingPolicyType.annuityHybrid,
    ])
  ) {
    // Annuity hybrids have a guaranteed minimum return (guaranteed accumulation value)
    // The total value should be at least this guaranteed amount, even if LTC isn't used
    if (checkPolicyCalculationTypeEquals(policyType, [FundingPolicyType.annuityHybrid])) {
      const guaranteedMinimum = ltcPolicy.policyMinimumGuaranteedBenefitAmount ?? 0;
      const projectedValue =
        totalProjectedCostCoverage + (isIndemnityPolicyPayment ? indemnitySurplus : 0);

      ltcPolicy.policyTotalValue = Math.max(projectedValue, guaranteedMinimum);
      ltcPolicy.policyNonLtcPayout = Math.max(
        0,
        ltcPolicy.policyTotalValue - totalProjectedCostCoverage,
      );
    } else {
      // Traditional LTC and STCI: total value is just the projected coverage
      ltcPolicy.policyNonLtcPayout = isIndemnityPolicyPayment ? indemnitySurplus : 0;
      ltcPolicy.policyTotalValue =
        totalProjectedCostCoverage + (isIndemnityPolicyPayment ? indemnitySurplus : 0);
    }
  }

  // For other policy types, the policy total value is the maximum benefit amount assuming that
  // whatever is leftover will be paid out in some form of death benefit to beneficiaries
  if (
    checkPolicyCalculationTypeEquals(policyType, [
      FundingPolicyType.hybridLifeInsurance,
      FundingPolicyType.lifeInsuranceWithRider,
      FundingPolicyType.hybridLifeIra,
    ])
  ) {
    const policyInflatedDeathBenefitAmount = calcPolicyInflatedMaxBenefitToEndOfPhase(
      mutableClient,
      CarePhase.fullCare,
      policyPremiumStartYear!,
      policyMaximumBenefitAmount ?? 0,
      policyInflationProtectionOnFaceAmount ?? policyInflationProtection ?? undefined,
      policyInflationProtectionYears,
    );

    ltcPolicy.policyInflatedDeathBenefitAmount = policyInflatedDeathBenefitAmount;

    // Calculate policy total value using the extracted function
    ltcPolicy.policyTotalValue = calcHybridPolicyTotalValue({
      ltcPolicy,
      policyInflatedDeathBenefitAmount,
      totalProjectedCostCoverage,
      indemnitySurplus,
    });

    // Calculate non ltc payout.
    // Simplifying assumption: assuming that this is the max benefit minus the projected coverage for hybrid and life with rider
    ltcPolicy.policyNonLtcPayout = (ltcPolicy.policyTotalValue ?? 0) - totalProjectedCostCoverage;
  }
}

// Helper types to make the code more readable
type PolicyInflationParams = {
  amount: number;
  inflationProtection: number;
};

function calcPolicyTotalLtcLimitForPhase(
  mutableClient: Draft<Client>,
  carePhase: CarePhase,
  unlimitedMaxBenefitMagicNumber: number,
  policyPremiumStartYear: number,
) {
  const {
    fundingSources: {
      ltcPolicy,
      ltcPolicy: {
        policyMaximumBenefitAmount,
        policyContinuationBenefitAmount,
        policyType,
        policyInflationProtection,
        policyInflationProtectionOnCOBAmount,
        policyInflationProtectionOnFaceAmount,
        policyInflationProtectionYears,
      },
    },
  } = mutableClient;

  // Early return for unlimited hybrid policies
  if (isHybridPolicy(policyType) && isUnlimitedBenefit(policyContinuationBenefitAmount)) {
    return handleUnlimitedBenefit(ltcPolicy, unlimitedMaxBenefitMagicNumber);
  }

  // Early return for unlimited standard policies
  if (!isHybridPolicy(policyType) && isUnlimitedBenefit(policyMaximumBenefitAmount)) {
    return handleUnlimitedBenefit(ltcPolicy, unlimitedMaxBenefitMagicNumber);
  }

  // Handle hybrid policy calculations
  if (isHybridPolicy(policyType)) {
    return calculateHybridPolicyBenefit({
      client: mutableClient,
      carePhase,
      policyPremiumStartYear,
      continuationBenefit: {
        amount: policyContinuationBenefitAmount!,
        inflationProtection: policyInflationProtectionOnCOBAmount ?? policyInflationProtection ?? 0,
      },
      deathBenefit: {
        amount: policyMaximumBenefitAmount ?? 0,
        inflationProtection:
          policyInflationProtectionOnFaceAmount ?? policyInflationProtection ?? 0,
      },
      policyInflationProtectionYears,
    });
  }

  // Handle standard policy calculations
  return calculateStandardPolicyBenefit({
    client: mutableClient,
    carePhase,
    policyPremiumStartYear,
    maximumBenefit: policyMaximumBenefitAmount!,
    hasInflationProtection: !!policyInflationProtection,
    policyInflationProtectionYears,
  });
}

// Helper functions
function isHybridPolicy(policyType: FundingPolicyType | null): boolean {
  return checkPolicyCalculationTypeEquals(policyType, [
    FundingPolicyType.hybridLifeInsurance,
    FundingPolicyType.hybridLifeIra,
  ]);
}

function isUnlimitedBenefit(amount: number | undefined | null): boolean {
  return isNullOrUndefined(amount);
}

function handleUnlimitedBenefit(
  ltcPolicy: Draft<PolicyFundingSource>,
  unlimitedMaxBenefitMagicNumber: number,
): number {
  ltcPolicy.policyBenefitIsUnlimited = true;
  return unlimitedMaxBenefitMagicNumber;
}

type HybridPolicyParams = {
  client: Draft<Client>;
  carePhase: CarePhase;
  policyPremiumStartYear: number;
  continuationBenefit: PolicyInflationParams;
  deathBenefit: PolicyInflationParams;
  policyInflationProtectionYears?: number | null;
};

function calculateHybridPolicyBenefit({
  client,
  carePhase,
  policyPremiumStartYear,
  continuationBenefit,
  deathBenefit,
  policyInflationProtectionYears,
}: HybridPolicyParams): number {
  const inflatedContinuationBenefitAmount = calcPolicyInflatedMaxBenefitToEndOfPhase(
    client,
    carePhase,
    policyPremiumStartYear,
    continuationBenefit.amount,
    continuationBenefit.inflationProtection,
    policyInflationProtectionYears,
  );

  const inflatedDeathBenefitAmount = calcPolicyInflatedMaxBenefitToEndOfPhase(
    client,
    carePhase,
    policyPremiumStartYear,
    deathBenefit.amount,
    deathBenefit.inflationProtection,
    policyInflationProtectionYears,
  );

  return inflatedContinuationBenefitAmount + inflatedDeathBenefitAmount;
}

type StandardPolicyParams = {
  client: Draft<Client>;
  carePhase: CarePhase;
  policyPremiumStartYear: number;
  maximumBenefit: number;
  hasInflationProtection: boolean;
  policyInflationProtectionYears?: number | null;
};

function calculateStandardPolicyBenefit({
  client,
  carePhase,
  policyPremiumStartYear,
  maximumBenefit,
  hasInflationProtection,
  policyInflationProtectionYears,
}: StandardPolicyParams): number {
  if (!hasInflationProtection) {
    return maximumBenefit;
  }

  return calcPolicyInflatedMaxBenefitToEndOfPhase(
    client,
    carePhase,
    policyPremiumStartYear,
    maximumBenefit,
    undefined,
    policyInflationProtectionYears,
  );
}

export function initializeRemainingFields(mutableClient: Draft<Client>) {
  const {
    fundingSources: { ltcPolicy },
  } = mutableClient;

  ltcPolicy.homeCareWaitingPeriodDaysRemaining = ltcPolicy.homeCareWaitingPeriodDays;
  ltcPolicy.facilityCareWaitingPeriodDaysRemaining = ltcPolicy.facilityCareWaitingPeriodDays;
  ltcPolicy.policyBenefitPeriodMonthsRemaining = ltcPolicy.policyBenefitPeriodMonths;
  ltcPolicy.epDaysServed = 0;
  ltcPolicy.epReimbursed = false;
}

export function calcCombinedTotalValue(mutableClient: Draft<Client>) {
  const {
    fundingSources,
    fundingSources: {
      annuity: { annuityTotalValue, isDeactivated: isAnnuityDeactivated },
    },
    multipleFundingSources: { multiplePolicyTotalValue, multipleSelfFundingTotalValue },
  } = mutableClient;

  fundingSources.combinedTotalValue =
    (multipleSelfFundingTotalValue ?? 0) +
    (multiplePolicyTotalValue ?? 0) +
    (isAnnuityDeactivated ? 0 : (annuityTotalValue ?? 0));
}

function addDiscountToReturnCashFlows(
  baseReturnCashFlows: CashFlow[],
  discountAmount: number,
): CashFlow[] {
  if (discountAmount <= 0 || baseReturnCashFlows.length === 0) {
    return baseReturnCashFlows;
  }

  // Distribute discount evenly across all return cash flows
  const discountPerCashFlow = discountAmount / baseReturnCashFlows.length;

  return baseReturnCashFlows.map(cashFlow => ({
    ...cashFlow,
    amount: cashFlow.amount + discountPerCashFlow,
  }));
}

export function calcPolicyProjectedROI(
  mutableClient: Draft<Client>,
  ltcPolicy: PolicyFundingSource,
  discountRate?: number,
): ReturnOnInvestmentCalculations & {
  policyProjectedReturnOnInvestment?: number;
  policyProjectedCompoundAnnualGrowthRate?: number;
  policyProjectedInternalRateOfReturn?: number;
  isIrrSuspect?: boolean;
} {
  const {
    intakeSurvey: { clientHasStartedLtc },
    clientPhasePredictedEndYears,
    allPhaseCosts: { allPhaseInflatedProfessionalShareCostDiscountAmount },
  } = mutableClient;

  const {
    policyTotalCost,
    policyCoverageOfRemainingLtcCosts,
    projectedCostCoverage,
    policyPremiumStartYear,
  } = ltcPolicy;

  const coverageAmount = clientHasStartedLtc
    ? policyCoverageOfRemainingLtcCosts
    : projectedCostCoverage;

  let predictedEndYear = clientPhasePredictedEndYears[CarePhase.fullCare];

  if (
    isNullOrUndefined(coverageAmount) ||
    isNullOrUndefined(policyTotalCost) ||
    isNullOrUndefined(predictedEndYear) ||
    isNullOrUndefined(policyPremiumStartYear)
  ) {
    return {
      investments: [],
      returns: [],
      investmentPeriodYears: 0,
    };
  }

  // If the policy is joint, use the max predicted end year
  if (
    ltcPolicy.policyType &&
    fundingPolicyTypeDefs[ltcPolicy.policyType].isJoint &&
    mutableClient.mutableClientPartner
  ) {
    predictedEndYear = Math.max(
      predictedEndYear,
      mutableClient.mutableClientPartner.clientPhasePredictedEndYears[CarePhase.fullCare] ?? 0,
    );
  }

  // Convert premiums and returns to cash flows
  const investmentCashFlows = convertPolicyPremiumsToCashFlows(ltcPolicy);
  const baseReturnCashFlows = convertPolicyReturnCashFlow(mutableClient);
  const returnCashFlows = addDiscountToReturnCashFlows(
    baseReturnCashFlows,
    allPhaseInflatedProfessionalShareCostDiscountAmount,
  );

  // Analyze the investment returns
  const { totalReturns, cagr, internalRateOfReturn, yearsToLastReturn, isIrrSuspect } =
    analyzeInvestmentReturns(investmentCashFlows, returnCashFlows, discountRate);

  return {
    investments: investmentCashFlows,
    returns: returnCashFlows,
    investmentPeriodYears: yearsToLastReturn,
    policyProjectedReturnOnInvestment: (totalReturns - policyTotalCost) / policyTotalCost,
    policyProjectedCompoundAnnualGrowthRate: cagr,
    policyProjectedInternalRateOfReturn: internalRateOfReturn,
    isIrrSuspect,
  };
}

function investmentCashflowEqualsPolicyTotalCost(
  investmentCashFlows: CashFlow[],
  policyTotalCost: number,
) {
  // test that total cash flows are equal to the policy total cost
  const totalInvestmentCashFlows = investmentCashFlows.reduce((sum, cf) => sum + cf.amount, 0);

  // Calculate percentage difference
  const percentDifference = Math.abs(
    ((totalInvestmentCashFlows - policyTotalCost) / policyTotalCost) * 100,
  );

  if (percentDifference > 1) {
    console.error('Investment cash flows do not match policy total cost', {
      totalInvestmentCashFlows,
      policyTotalCost,
      difference: totalInvestmentCashFlows - policyTotalCost,
      percentDifference: `${percentDifference.toFixed(4)}%`,
      cashFlows: investmentCashFlows.map(cf => ({
        year: cf.year,
        amount: cf.amount,
        runningTotal: investmentCashFlows
          .filter(f => f.year <= cf.year)
          .reduce((sum, f) => sum + f.amount, 0),
      })),
    });
  }
}

function returnCashflowEqualsPolicyTotalValue(
  returnCashFlows: CashFlow[],
  policyTotalValue: number | undefined,
) {
  if (isNullOrUndefined(policyTotalValue)) {
    return;
  }

  // Sum all return cash flows
  const totalReturnCashFlows = returnCashFlows.reduce((sum, cf) => sum + cf.amount, 0);

  // Calculate percentage difference
  const percentDifference = Math.abs(
    ((totalReturnCashFlows - policyTotalValue) / policyTotalValue) * 100,
  );

  if (percentDifference > 1) {
    console.error('Return cash flows do not match policy total value', {
      totalReturnCashFlows,
      policyTotalValue,
      difference: totalReturnCashFlows - policyTotalValue,
      percentDifference: `${percentDifference.toFixed(4)}%`,
      cashFlows: returnCashFlows.map(cf => ({
        year: cf.year,
        amount: cf.amount,
        runningTotal: returnCashFlows
          .filter(f => f.year <= cf.year)
          .reduce((sum, f) => sum + f.amount, 0),
      })),
    });
  }
}

export function calcPolicyTotalCost(
  mutableClient: Draft<Client>,
  premiumPayThroughMonthsOverride?: number,
) {
  const {
    fundingSources: {
      ltcPolicy,
      ltcPolicy: {
        policyPremiumStartYear,
        policyLimitedPayYears,
        policyType,
        policyPremiumIncreaseRate,
        policyPremiumIncreaseYears,
      },
    },
    appliedInferenceSet: { ltcAtYear },
  } = mutableClient;

  if (isNullOrUndefined(policyPremiumStartYear)) {
    ltcPolicy.policyTotalCost = 0;
    return;
  }

  // Premiums run every month from policy issue until benefits BEGIN — through the
  // out-of-pocket window (elimination period + the <2-ADL not-yet-eligible
  // months), not stopping at care onset (`ltcAtYear`). The period is expressed in
  // YEARS here (fractional allowed); `convertPolicyPremiumsToCashFlows` lays whole-
  // year buckets plus a final partial bucket for the leftover months.
  //
  //  • Projection path: the caller passes `premiumPayThroughMonthsOverride` — the
  //    projection's exact months from issue to the first paying month, measured on
  //    its integer-year clock anchored at the START of the issue year (NO current-
  //    month proxy). Used directly, so the premium total equals that months-from-
  //    issue figure to the month — the single source of truth shared with the
  //    projection-details UI — and the final partial bucket is the out-of-pocket
  //    months of the payout policy year before benefits begin.
  //  • Membership (CCAH — Fortified Life, Stay Your Way): dues run for the LIFE OF
  //    THE MEMBER, through the end of care (`calcLtcEndYear`), regardless of the
  //    override. Whole care years, so no partial bucket.
  //  • Legacy fallback (no override — engine off / policy never pays): stop at care
  //    onset via `calculateMonthsBetweenYears`, which keeps its own current-year
  //    elapsed-months handling for the legacy path.
  const totalYears = isMembershipProgram(policyType)
    ? Math.max(calcLtcEndYear(mutableClient) - policyPremiumStartYear, 0)
    : premiumPayThroughMonthsOverride != null
      ? premiumPayThroughMonthsOverride / 12
      : calculateMonthsBetweenYears(policyPremiumStartYear, ltcAtYear!) / 12;

  // Clamp at zero: a legacy current-year policy whose care begins this year makes
  // `calculateMonthsBetweenYears` negative, which means "no recurring premiums" —
  // not a sign-flipped partial-year bucket. `policyLimitedPayYears` caps the pay
  // period (in years) when set.
  const premiumInvestmentPeriodYears = Math.max(
    0,
    policyLimitedPayYears ? Math.min(policyLimitedPayYears, totalYears) : totalYears,
  );

  ltcPolicy.premiumInvestmentPeriodMonths = premiumInvestmentPeriodYears * 12;

  // Persist the applied increase-year cap so convertPolicyPremiumsToCashFlows
  // reproduces the same step-up schedule when it builds the premium stream.
  const hasIncreaseRate = !!policyPremiumIncreaseRate && policyPremiumIncreaseRate > 0;
  if (hasIncreaseRate) {
    ltcPolicy.appliedPolicyPremiumIncreaseYears =
      policyPremiumIncreaseYears && policyPremiumIncreaseYears > 0
        ? Math.min(policyPremiumIncreaseYears, premiumInvestmentPeriodYears)
        : premiumInvestmentPeriodYears;
  }

  // Single source of truth: derive policyTotalCost by summing the exact premium
  // cash-flow stream (lump sum + recurring premiums) that downstream ROI and the
  // investmentCashflowEqualsPolicyTotalCost reconciliation check also consume.
  // Computing the total any other way (e.g. a parallel closed form) lets it drift
  // from those cash flows for mid-year / fractional premium periods.
  const lumpSumPayment =
    isNullOrUndefined(ltcPolicy.policyLumpSumPayment) || ltcPolicy.policyLumpSumPayment < 0
      ? 0
      : ltcPolicy.policyLumpSumPayment;
  const totalCost = convertPolicyPremiumsToCashFlows(ltcPolicy).reduce(
    (sum, cf) => sum + cf.amount,
    0,
  );

  // No clamp here: policyTotalCost must equal the stream sum exactly, or the
  // investmentCashflowEqualsPolicyTotalCost reconciliation drifts. The stream
  // only contains the lump sum plus non-negative premium buckets, so the
  // recurring portion is non-negative by construction.
  ltcPolicy.monthlyPremiumTotalCost = totalCost - lumpSumPayment;
  ltcPolicy.policyTotalCost = totalCost;
}

function copyJointPolicyToClientPartner(mutableClient: Draft<Client>): void {
  const {
    fundingSources: { ltcPolicy },
    mutableClientPartner,
  } = mutableClient;

  if (!isJointPolicy(ltcPolicy.policyType, mutableClientPartner)) {
    return;
  }
  const ltcPolicyCopy = cloneDeep(ltcPolicy);
  mutableClientPartner.fundingSources.ltcPolicy = ltcPolicyCopy;
}

type ClientSinglePhaseCosts = {
  client: Client;
  phaseStartYear: number | null;
  phaseCosts: SinglePhaseCosts;
};

export function combineSortClientPartnerPhaseCosts(
  mutableClient: Draft<Client>,
): ClientSinglePhaseCosts[] {
  const { phaseCosts } = mutableClient;

  if (!mutableClient.mutableClientPartner) {
    return [];
  }
  // extract partner data
  const mutableClientPartner = mutableClient.mutableClientPartner;
  const partnerPhaseCosts = mutableClientPartner.phaseCosts;

  // phase start years
  const clientPhaseStartYears = calcClientPhaseStartYears(mutableClient);
  const partnerPhaseStartYears = calcClientPhaseStartYears(mutableClientPartner);

  // order client and partner phases chronologically
  const combinedPhaseCosts: Array<ClientSinglePhaseCosts> = [
    ...phaseCosts.map(pc => ({
      client: mutableClient,
      phaseStartYear: clientPhaseStartYears[pc.carePhase],
      phaseCosts: pc,
    })),
    ...partnerPhaseCosts.map(pc => ({
      client: mutableClientPartner,
      phaseStartYear: partnerPhaseStartYears[pc.carePhase],
      phaseCosts: pc,
    })),
  ];

  combinedPhaseCosts.sort((a, b) => {
    const startYearA = a.phaseStartYear ?? Infinity;
    const startYearB = b.phaseStartYear ?? Infinity;

    return startYearA - startYearB;
  });

  return combinedPhaseCosts.filter(
    clientPhaseCost =>
      !isCarePhasePast(clientPhaseCost.phaseCosts.carePhase, clientPhaseCost.client),
  );
}
export function calcMultiplePolicyTotalFields(mutableClient: WritableDraft<Client>) {
  const {
    intakeSurvey: { clientHasStartedLtc },
    multipleFundingSources,
  } = mutableClient;

  const activeLtcPolicies = Object.values(multipleFundingSources.ltcPolicy).filter(
    ltcPolicy => !ltcPolicy.isDeactivated,
  );

  multipleFundingSources.multiplePolicyTotalValue = sumPropertyForAllPolicies(
    'policyTotalValue',
    activeLtcPolicies,
  );
  multipleFundingSources.multiplePolicyTotalCost = sumPropertyForAllPolicies(
    'policyTotalCost',
    activeLtcPolicies,
  );
  multipleFundingSources.multiplePolicyNonLtcPayout = sumPropertyForAllPolicies(
    'policyNonLtcPayout',
    activeLtcPolicies,
  );

  multipleFundingSources.multiplePolicyProjectedCostCoverage = sumPropertyForAllPolicies(
    coverageKeyFor(clientHasStartedLtc),
    activeLtcPolicies,
  );

  // check for any unlimited policies
  const unlimitedPolicies = activeLtcPolicies.filter(policy => policy.policyBenefitIsUnlimited);

  multipleFundingSources.multiplePolicyTotalLtcBenefit =
    unlimitedPolicies.length > 0
      ? Infinity
      : sumPropertyForAllPolicies('policyTotalLTCBenefit', activeLtcPolicies);
}

function sumPropertyForAllSelfFundingSources(
  property: keyof SelfFundingSource,
  selfFundingSources: WritableDraft<SelfFundingSource>[],
): number | undefined {
  // check if all values are undefined
  if (selfFundingSources.every(source => source[property] === undefined)) {
    return undefined;
  }
  return selfFundingSources.reduce((acc, source) => {
    const value = source[property];
    return acc + (typeof value === 'number' ? value : 0);
  }, 0);
}

export function calcMultipleSelfFundingTotalFields(mutableClient: WritableDraft<Client>) {
  const { multipleFundingSources } = mutableClient;

  const activeSelfFundingSources = Object.values(multipleFundingSources.selfFunding).filter(
    selfFunding => !selfFunding.isDeactivated,
  );

  multipleFundingSources.multipleSelfFundingTotalValue = sumPropertyForAllSelfFundingSources(
    'selfFundingTotalValue',
    activeSelfFundingSources,
  );
  multipleFundingSources.multipleSelfFundingTotalCost = sumPropertyForAllSelfFundingSources(
    'selfFundingTotalCost',
    activeSelfFundingSources,
  );
  multipleFundingSources.multipleSelfFundingNonLtcPayout = sumPropertyForAllSelfFundingSources(
    'selfFundingNonLtcPayout',
    activeSelfFundingSources,
  );
  multipleFundingSources.multipleSelfFundingProjectedCostCoverage =
    sumPropertyForAllSelfFundingSources(
      'selfFundingProjectedCostCoverage',
      activeSelfFundingSources,
    );
}

export function convertPolicyPremiumsToCashFlows(ltcPolicy: PolicyFundingSource): CashFlow[] {
  const {
    policyLumpSumPayment,
    policyPremiumMonthlyCost,
    premiumInvestmentPeriodMonths,
    policyPremiumIncreaseRate,
    appliedPolicyPremiumIncreaseYears,
    policyPremiumStartYear,
  } = ltcPolicy;
  const cashFlows: CashFlow[] = [];

  if (isNullOrUndefined(policyPremiumStartYear)) {
    return cashFlows;
  }

  // Lump sum is paid in the policy premium start year
  if (!isNullOrUndefined(policyLumpSumPayment) && policyLumpSumPayment > 0) {
    cashFlows.push({
      year: policyPremiumStartYear,
      amount: policyLumpSumPayment,
    });
  }

  if (
    isNullOrUndefined(policyPremiumMonthlyCost) ||
    isNullOrUndefined(premiumInvestmentPeriodMonths) ||
    policyPremiumMonthlyCost <= 0 ||
    // Negative periods (clamped away in calcPolicyTotalCost, but guard direct
    // callers too) would sign-flip the remainder into a phantom partial bucket.
    premiumInvestmentPeriodMonths <= 0
  ) {
    return cashFlows;
  }

  const fullYears = Math.floor(premiumInvestmentPeriodMonths / 12);
  // Exact fractional remainder (do NOT round). calcPolicyTotalCost sums this
  // very stream for policyTotalCost, so rounding the partial year here would
  // reintroduce drift between the two. Whole-year periods yield 0 (no partial
  // bucket); membership/CCRC periods that end mid-year keep their true fraction.
  const remainingMonths = premiumInvestmentPeriodMonths - fullYears * 12;
  // Ignore sub-cent floating-point dust (calcLtcEndYear round-trips months
  // through ÷12…×12, so a "whole-year" period can land on e.g. 36.0000000001)
  // to avoid emitting a meaningless near-zero final cash flow.
  const hasPartialYear = remainingMonths > 1e-6;
  const monthlyAmount = policyPremiumMonthlyCost;
  const hasIncreaseRate = policyPremiumIncreaseRate && policyPremiumIncreaseRate > 0;

  // Monthly premiums begin the year after the policy starts (preserving the
  // pre-existing convention where year 0 was the lump sum and year 1 was the
  // first full premium year). This +1 timing offset is an ROI/IRR modeling
  // convention, NOT a display year — the client's `buildPremiumLookup`
  // (premiumSchedule.ts) un-shifts it by one year so the rendered premium columns
  // land on the correct calendar/policy years.
  //
  // Membership programs (CCAH — Fortified Life, Stay Your Way) are NOT
  // special-cased here even though their dues run through care: they ride the
  // exact same +1 convention, and the client un-shift compensates identically. An
  // earlier attempt to anchor membership at `policyPremiumStartYear` (no +1) broke
  // the membership display — the un-shift then dropped the final care year and
  // double-counted policy year 1. Keep this in sync with `buildPremiumLookup`; do
  // NOT branch on policy type here.
  const firstPremiumYear = policyPremiumStartYear + 1;

  if (!hasIncreaseRate) {
    for (let year = 0; year < fullYears; year++) {
      cashFlows.push({
        year: firstPremiumYear + year,
        amount: monthlyAmount * 12,
      });
    }
    if (hasPartialYear) {
      cashFlows.push({
        year: firstPremiumYear + fullYears,
        amount: monthlyAmount * remainingMonths,
      });
    }
  } else {
    // Clamp the step-ups to [0, fullYears] whole years.
    // appliedPolicyPremiumIncreaseYears can be fractional (it defaults to the
    // fractional premium period when no increase-years limit is set, and policy
    // extraction stores unvalidated floats), and calcTotalWithAnnualIncrease's
    // per-year loop would round a fractional value up to an extra full step-up
    // year, double-counting premium for the partial final year. A negative
    // persisted value would make the same loop over-count remaining years at
    // negative year offsets, hence the zero floor. The partial year is billed
    // separately below at base × (1 + rate)^stepUpYears — when the step-ups run
    // the whole pay period (stepUpYears === fullYears) that is one anniversary
    // step-up ABOVE the last whole year's rate; when capped earlier it holds
    // the last whole-year rate.
    const stepUpYears = Math.max(
      0,
      Math.min(Math.floor(appliedPolicyPremiumIncreaseYears ?? fullYears), fullYears),
    );

    // calcTotalWithAnnualIncrease returns cashflows offset by 1 (year 1..N).
    // Shift them so year 1 lands on firstPremiumYear.
    const { cashflows } = calcTotalWithAnnualIncrease(
      monthlyAmount,
      policyPremiumIncreaseRate,
      stepUpYears,
      fullYears,
    );

    cashflows.forEach(cf =>
      cashFlows.push({ year: firstPremiumYear + cf.year - 1, amount: cf.amount }),
    );

    if (hasPartialYear) {
      const finalMonthlyAmount =
        monthlyAmount * Math.pow(1 + policyPremiumIncreaseRate, stepUpYears);
      cashFlows.push({
        year: firstPremiumYear + fullYears,
        amount: finalMonthlyAmount * remainingMonths,
      });
    }
  }

  return cashFlows;
}

function convertPolicyReturnCashFlow(mutableClient: Draft<Client>): CashFlow[] {
  const {
    clientPhasePredictedEndYears,
    phaseCosts,
    fundingSources: {
      ltcPolicy: { policyTotalValue, policyPremiumStartYear, policyType },
    },
    mutableClientPartner,
  } = mutableClient;

  const phaseStartYears = calcClientPhaseStartYears(mutableClient);

  // Early return if required values are missing
  if (isNullOrUndefined(policyTotalValue) || isNullOrUndefined(policyPremiumStartYear)) {
    return [];
  }

  const allCarePhases = [CarePhase.earlyCare, CarePhase.moderateCare, CarePhase.fullCare];
  const jointPolicy = isJointPolicy(policyType, mutableClientPartner);

  // Get phases with coverage for a client
  const getPhasesWithCoverage = (client: Draft<Client>) => {
    // Early return if phaseCalculations is undefined or empty
    if (!client.phaseCalculations || !Array.isArray(client.phaseCalculations)) {
      return [];
    }

    return allCarePhases.filter(phase => {
      const phaseCalc = client.phaseCalculations.find(
        calc => calc && typeof calc === 'object' && calc.phase === phase,
      );
      return phaseCalc && 'phaseCostCoverage' in phaseCalc && phaseCalc.phaseCostCoverage > 0;
    });
  };

  // Get client's phases with coverage
  let clientPhasesWithCoverage = getPhasesWithCoverage(mutableClient);

  // Handle single policy case
  if (!jointPolicy || !mutableClientPartner) {
    if (clientPhasesWithCoverage.length === 0 && !policyTotalValue) return [];

    // If no phases with coverage, but policy total value exists, assume all returns are after last phase
    if (clientPhasesWithCoverage.length === 0) {
      clientPhasesWithCoverage = [CarePhase.fullCare];
    }
    const valuePerPhase = policyTotalValue / clientPhasesWithCoverage.length;
    return createCashFlowsForPhasePayout({
      phases: clientPhasesWithCoverage,
      valuePerPhase,
      predictedEndYears: clientPhasePredictedEndYears,
      predictedStartYears: phaseStartYears,
      phaseCosts,
      policyPremiumStartYear,
    });
  }

  // Handle joint policy case
  const partnerPhasesWithCoverage = getPhasesWithCoverage(mutableClientPartner);
  const allPhasesWithCoverage = [...clientPhasesWithCoverage, ...partnerPhasesWithCoverage];
  const partnerPhaseStartYears = calcClientPhaseStartYears(mutableClientPartner);

  if (allPhasesWithCoverage.length === 0) return [];

  const valuePerPhase = policyTotalValue / allPhasesWithCoverage.length;
  return [
    ...createCashFlowsForPhasePayout({
      phases: clientPhasesWithCoverage,
      valuePerPhase,
      predictedEndYears: clientPhasePredictedEndYears,
      predictedStartYears: phaseStartYears,
      phaseCosts,
      policyPremiumStartYear,
    }),
    ...createCashFlowsForPhasePayout({
      phases: partnerPhasesWithCoverage,
      valuePerPhase,
      predictedEndYears: mutableClientPartner.clientPhasePredictedEndYears,
      predictedStartYears: partnerPhaseStartYears,
      phaseCosts: mutableClientPartner.phaseCosts,
      policyPremiumStartYear,
    }),
  ];
}

// Helper function to create cash flows for a set of phases
interface CreateCashFlowsParams {
  phases: CarePhase[];
  valuePerPhase: number;
  predictedEndYears: Record<CarePhase, number | null>;
  predictedStartYears: Record<CarePhase, number | null>;
  phaseCosts: SinglePhaseCosts[];
  policyPremiumStartYear: number;
}

function createCashFlowsForPhasePayout({
  phases,
  valuePerPhase,
  predictedEndYears,
  predictedStartYears,
  phaseCosts,
  policyPremiumStartYear,
}: CreateCashFlowsParams): CashFlow[] {
  const cashFlows: CashFlow[] = [];

  phases.forEach(phase => {
    const endYear = predictedEndYears[phase];
    const startYear = predictedStartYears[phase];

    if (isNullOrUndefined(endYear) || isNullOrUndefined(startYear)) {
      return;
    }

    // Find the phase cost data to get duration in months
    const phaseCostData = phaseCosts.find(pc => pc.carePhase === phase);
    const durationMonths = phaseCostData?.phaseCareMonthsNeeded ?? 0;

    if (durationMonths === 0) {
      // If no duration data, fall back to a single lump-sum payout at the
      // phase end year (clamped to never predate the policy).
      cashFlows.push({
        year: Math.max(policyPremiumStartYear, Math.ceil(endYear)),
        amount: valuePerPhase,
      });
      return;
    }

    // Spread payout over the phase duration
    const durationYears = durationMonths / 12;
    const numYears = Math.ceil(durationYears);
    const yearlyPayout = valuePerPhase / durationYears;

    for (let yearOffset = 0; yearOffset < numYears; yearOffset++) {
      const payoutYear = startYear + yearOffset;
      const isLastYear = yearOffset === numYears - 1;

      // In the last year, pay the remainder to avoid rounding issues
      const amount = isLastYear ? valuePerPhase - yearlyPayout * yearOffset : yearlyPayout;

      cashFlows.push({
        year: Math.max(policyPremiumStartYear, Math.round(payoutYear)),
        amount: amount,
      });
    }
  });

  return cashFlows;
}

export function calcReturnOnInvestment(mutableClient: WritableDraft<Client>) {
  const {
    multipleFundingSources,
    allPhaseCosts: { allPhaseInflatedProfessionalShareCostDiscountAmount },
  } = mutableClient;
  const { combinedTotalValue, combinedFundingTotalCost } = mutableClient.fundingSources;
  const { fundingSources } = mutableClient;
  const discountRate = mutableClient.clientCalculationSettings?.discountRate ?? undefined;

  // Calculate self-funding ROI by aggregating all self-funding sources
  const selfFundingInvestments: CashFlow[] = [];
  const selfFundingReturns: CashFlow[] = [];
  const selfFundingInvestmentPeriodYears: number[] = [];
  const selfFundingSources = Object.entries(multipleFundingSources.selfFunding);

  selfFundingSources.forEach(([_fundingSourceId, selfFundingSource]) => {
    if (selfFundingSource.isDeactivated) return;
    // Income streams are not investments — skip ROI aggregation
    if (getCalcConfig(selfFundingSource.accountType).calcMethod === 'income-stream') return;

    // Temporarily set the selfFunding source to calculate its cash flows
    mutableClient.fundingSources.selfFunding = cloneDeep(selfFundingSource);

    const { investments, returns, investmentPeriodYears } = calcSelfFundingProjectedROI(
      mutableClient,
      discountRate,
    );

    selfFundingInvestments.push(...investments);
    selfFundingReturns.push(...returns);
    selfFundingInvestmentPeriodYears.push(investmentPeriodYears);
  });

  const {
    investments: annuityInvestments,
    returns: annuityReturns,
    investmentPeriodYears: annuityInvestmentPeriodYears,
  } = calcAnnuityProjectedROI(mutableClient, discountRate);

  // Calculate LTC policy ROI
  const ltcPolicyInvestments: CashFlow[] = [];
  const ltcPolicyReturns: CashFlow[] = [];
  const ltcPolicyInvestmentPeriodYears: number[] = [];
  const ltcPolicies = Object.entries(multipleFundingSources.ltcPolicy);
  ltcPolicies.forEach(([_fundingSourceId, policyFundingSource]) => {
    if (policyFundingSource.isDeactivated) {
      return;
    }
    ltcPolicyInvestments.push(...(policyFundingSource.investmentCashFlows ?? []));
    ltcPolicyReturns.push(...(policyFundingSource.returnCashFlows ?? []));
    ltcPolicyInvestmentPeriodYears.push(policyFundingSource.investmentPeriodYears ?? 0);
  });

  // Calculate combined ROI
  if (!combinedFundingTotalCost || !combinedTotalValue || combinedFundingTotalCost === 0) {
    fundingSources.combinedProjectedReturnOnInvestment = 0;
    fundingSources.combinedProjectedROIAnnualized = 0;
    fundingSources.combinedProjectedInternalRateOfReturn = 0;
    fundingSources.combinedIsIrrSuspect = false;
    return;
  }

  const policyInvestmentPeriods = Object.values(multipleFundingSources.ltcPolicy)
    .filter(policy => !policy.isDeactivated)
    .map(policy => policy.investmentPeriodYears ?? 0);

  const maximumInvestmentPeriodYears = Math.max(
    ...policyInvestmentPeriods,
    ...selfFundingInvestmentPeriodYears,
    annuityInvestmentPeriodYears ?? 0,
    0,
  );

  fundingSources.combinedInvestmentPeriodYears = maximumInvestmentPeriodYears;
  fundingSources.combinedProjectedReturnOnInvestment =
    (combinedTotalValue +
      allPhaseInflatedProfessionalShareCostDiscountAmount -
      combinedFundingTotalCost) /
    combinedFundingTotalCost;

  const analysis = analyzeInvestmentReturns(
    [...selfFundingInvestments, ...annuityInvestments, ...ltcPolicyInvestments],
    [...selfFundingReturns, ...annuityReturns, ...ltcPolicyReturns],
    discountRate,
  );

  fundingSources.combinedProjectedCompoundAnnualGrowthRate = analysis.cagr;
  fundingSources.combinedProjectedInternalRateOfReturn = analysis.internalRateOfReturn;
  fundingSources.combinedIsIrrSuspect = analysis.isIrrSuspect;
}
